import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import BookingLanding from "./BookingLanding.jsx";
import { buildSlots, ymdLocal, nowInTimeZone } from "../slotBuilder.js";
import { validateForm, mergeQuestionsById, resolveAutofilledQuestions } from "../formValidation.js";
import { resolveStaffChoice } from "../staffChoice.js";

// ═════════════════════════════════════════════════════════════════════════════
// RESPONSIVE HOOK
// ═════════════════════════════════════════════════════════════════════════════
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

// ═════════════════════════════════════════════════════════════════════════════
// CONFIG  — non-Graph metadata: routing rules, visual enrichment, ERP list.
// Graph supplies the canonical services + staff. This supplies everything else.
// ═════════════════════════════════════════════════════════════════════════════
const CONFIG = {
  org: "Mekorma",
  tagline: "Schedule time with our team",

  // Relationship types are now sourced dynamically from the Lead entity's
  // mek_relationshiptype OptionSet via /api/lead-relationship-types.

  // Maps relationship type codes → Graph service displayNames that appear for that type.
  // If a relationship type is not listed or its array is empty, ALL services are shown.
  // In production: store in a Dataverse routing-rules table, fetch at bootstrap.
  serviceRouting: {
    PROSPECT: ["UTM Demo Event", "Demo", "Demo 2"],
    PARTNER:  ["UTM Demo Event", "UTM Demo Social", "Azure Support"],
    EXISTING: ["Azure Support",  "IT support",      "Test Sync Meeting"],
    INTERNAL: ["Demo", "Demo 2", "Test Sync Meeting", "UTM Demo Event", "UTM Demo Social", "Azure Support", "IT support"],
  },

  // Visual + UX metadata keyed by Graph service displayName.
  // Accent colour and staff-selection mode are not in Graph — we own these.
  // Services not in this map get auto-generated defaults.
  serviceMeta: {
    "UTM Demo Event":    { accent: "#004e42", staffMode: "UserSelectable" },
    "UTM Demo Social":   { accent: "#006b5c", staffMode: "UserSelectable" },
    "Demo 2":            { accent: "#0a7a68", staffMode: "UserSelectable" },
    "Demo":              { accent: "#88c96c", staffMode: "UserSelectable" },
    "Test Sync Meeting": { accent: "#5a9e3b", staffMode: "UserSelectable" },
    "Azure Support":     { accent: "#2d7a5f", staffMode: "UserSelectable" },
    "IT support":        { accent: "#1a6b36", staffMode: "AnyAvailable"   },
  },

  // Staff enrichment keyed by emailAddress (lowercase).
  // Graph gives us displayName + email + role; everything else lives here.
  // In production: maintain in Dataverse staff-mapping table.
  staffMeta: {
    "daniel@mekorma.com":  { title: "Administrator",          initials: "DL", bio: "Mekorma IT team lead and Bookings administrator.",                    avatarBg: "#E8F2EF", avatarFg: "#003a31" },
    "ernesto@mekorma.com": { title: "Administrator",          initials: "EA", bio: "Mekorma platform and integrations specialist.",                       avatarBg: "#EBF7EC", avatarFg: "#1a6b36" },
    "peterv@mekorma.com":  { title: "Administrator",          initials: "PV", bio: "Mekorma systems administrator and cloud infrastructure.",             avatarBg: "#F0FAF0", avatarFg: "#5a9e3b" },
    "samuel@mekorma.com":  { title: "Administrator",          initials: "SA", bio: "Mekorma development and support engineer.",                           avatarBg: "#E8F5F3", avatarFg: "#006b5c" },
  },

  erpSystems: [
    "Dynamics 365 Business Central","Dynamics GP","Dynamics SL",
    "Sage Intacct","NetSuite","QuickBooks Enterprise","SAP Business One","Acumatica","Other",
  ],
};

// ═════════════════════════════════════════════════════════════════════════════
// MICROSOFT GRAPH  —  BOOKINGS API LAYER
//
// Each function calls the backend proxy at /api/... which handles MSAL auth.
// No tokens on the frontend — all Graph calls go through Azure Functions.
// ═════════════════════════════════════════════════════════════════════════════

// ── ISO 8601 duration → minutes  ("PT30M"→30, "PT1H"→60, "PT1H30M"→90) ──────
const parseDuration = iso => {
  if (!iso) return 60;
  const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  return m ? (parseInt(m[1]||0)*60 + parseInt(m[2]||0)) : 60;
};

// ── 1. Services  →  GET /v1.0/solutions/bookingBusinesses/{id}/services ───────
// businessId is optional; omitted = use the server's configured default
// (first entry of settings.integrations.bookingsBusinessEmails → BOOKING_BUSINESS_ID).
async function graphGetServices(businessId) {
  const qs = businessId ? `?businessId=${encodeURIComponent(businessId)}` : "";
  console.log(`[BookingPlatform] Fetching services from /api/services${qs}`);
  const startMs = performance.now();
  const r = await fetch(`/api/services${qs}`);
  const elapsed = Math.round(performance.now() - startMs);
  console.log(`[BookingPlatform] /api/services responded ${r.status} in ${elapsed}ms`);
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    console.error(`[BookingPlatform] /api/services error:`, r.status, body);
    const e = new Error(body.error || "We couldn't load available services. Please try again.");
    e.correlationId = body.correlationId;
    throw e;
  }
  const data = await r.json();
  const services = data.value ?? [];
  console.log(`[BookingPlatform] Received ${services.length} service(s):`, services.map(s => `${s.displayName} (${s.id})`));
  return services;
}

// ── 2. Staff for a service  →  GET .../staffMembers  (filter by ids) ──────────
async function graphGetStaffForService(businessId, staffMemberIds) {
  const ids = staffMemberIds.join(",");
  const bizQs = businessId ? `&businessId=${encodeURIComponent(businessId)}` : "";
  console.log(`[BookingPlatform] Fetching staff from /api/staff?ids=${ids}${bizQs}`);
  const startMs = performance.now();
  const r = await fetch(`/api/staff?ids=${encodeURIComponent(ids)}${bizQs}`);
  const elapsed = Math.round(performance.now() - startMs);
  console.log(`[BookingPlatform] /api/staff responded ${r.status} in ${elapsed}ms`);
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    console.error(`[BookingPlatform] /api/staff error:`, r.status, body);
    const e = new Error(body.error || "We couldn't load available team members. Please try again.");
    e.correlationId = body.correlationId;
    throw e;
  }
  const data = await r.json();
  const staff = data.value ?? [];
  console.log(`[BookingPlatform] Received ${staff.length} staff member(s):`, staff.map(s => `${s.displayName} (${s.id})`));
  return staff;
}

// ── 3. Availability  →  POST .../getStaffAvailability ─────────────────────────
async function graphGetStaffAvailability(businessId, staffIds, date, timezone, serviceId, endDate) {
  const dateStr = date instanceof Date ? ymdLocal(date) : date;
  const body = { staffIds, date: dateStr, timezone, ...(businessId ? { businessId } : {}), ...(serviceId ? { serviceId } : {}), ...(endDate ? { endDate } : {}) };
  console.log(`[BookingPlatform] Fetching staff availability:`, body);
  const startMs = performance.now();
  const r = await fetch(`/api/staff-availability`, {
    method: "POST",
    cache: "no-store", // live data — never from the HTTP cache
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const elapsed = Math.round(performance.now() - startMs);
  console.log(`[BookingPlatform] /api/staff-availability responded ${r.status} in ${elapsed}ms`);
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    console.error(`[BookingPlatform] /api/staff-availability error:`, r.status, body);
    const e = new Error(body.error || "We couldn't load available times. Please try again.");
    e.correlationId = body.correlationId;
    throw e;
  }
  const data = await r.json();
  console.log(`[BookingPlatform] Availability received for ${data?.staffAvailabilityItems?.length ?? 0} staff member(s)`);
  for (const item of data?.staffAvailabilityItems ?? []) {
    console.log(`[BookingPlatform]   staffId=${item.staffId}: ${item.availabilityItems?.length ?? 0} segment(s)`);
  }
  return data;
}

// ── 3b. Service availability policy  →  GET /api/service-availability ────────
// Which dates the service can be booked on and its bookable hours per day, in
// the viewer's timezone (Graph's staff availability doesn't include this).
async function graphGetServiceAvailability(service, timezone, from, to) {
  const qs = new URLSearchParams({ serviceId: service.id, timezone, from, to });
  if (service.businessId) qs.set("businessId", service.businessId);
  const r = await fetch(`/api/service-availability?${qs}`, { cache: "no-store" });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    console.error(`[BookingPlatform] /api/service-availability error:`, r.status, body);
    const e = new Error(body.error || "We couldn't load this service's availability.");
    e.correlationId = body.correlationId;
    throw e;
  }
  const data = await r.json();
  console.log(`[BookingPlatform] Service availability ${from}..${to}: ${Object.values(data.days || {}).filter(d => d.open).length} open day(s), interval=${data.policy?.timeSlotIntervalMins ?? "default"}min`);
  return data;
}

// ── 4. Create appointment  →  POST .../appointments ───────────────────────────
async function graphCreateAppointment(payload) {
  console.log(`[BookingPlatform] Creating appointment via /api/booking/create:`, {
    serviceId: payload.serviceId,
    staffMemberId: payload.staffMemberId,
    startDateTime: payload.startDateTime,
    attendeeEmail: payload.attendee?.email,
  });
  const startMs = performance.now();
  const r = await fetch(`/api/booking/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const elapsed = Math.round(performance.now() - startMs);
  console.log(`[BookingPlatform] /api/booking/create responded ${r.status} in ${elapsed}ms`);
  if (!r.ok && r.status !== 207) {
    const body = await r.json().catch(() => ({}));
    console.error(`[BookingPlatform] /api/booking/create error:`, r.status, body);
    const e = new Error(body.error || "We couldn't complete your booking. Please try again.");
    e.code = body.code;
    e.correlationId = body.correlationId;
    throw e;
  }
  const data = await r.json();
  console.log(`[BookingPlatform] Appointment result:`, data);
  return data;
}

// ── 5. Cancel appointment  →  POST .../appointments/{id}/cancel ──────────────
// businessId = the Bookings business that owns the appointment (the service's);
// omitted ⇒ the server's default business.
async function graphCancelAppointment(appointmentId, businessId, cancelToken) {
  console.log(`[BookingPlatform] Cancelling appointment via /api/booking/cancel:`, appointmentId, businessId || "(default business)");
  const startMs = performance.now();
  const r = await fetch(`/api/booking/cancel`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ appointmentId, cancelToken, ...(businessId ? { businessId } : {}) }),
  });
  const elapsed = Math.round(performance.now() - startMs);
  console.log(`[BookingPlatform] /api/booking/cancel responded ${r.status} in ${elapsed}ms`);
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    console.error(`[BookingPlatform] /api/booking/cancel error:`, r.status, body);
    const e = new Error(body.error || "We couldn't cancel this appointment. Please try again.");
    e.correlationId = body.correlationId;
    throw e;
  }
  return r.json();
}

// ═════════════════════════════════════════════════════════════════════════════
// MOCK DATA  —  Graph-shaped responses
// ═════════════════════════════════════════════════════════════════════════════

const MOCK_SERVICES = [
  { id:"svc-001", displayName:"Discovery Call",         description:"Focused 30-minute conversation about your AP automation goals and current ERP environment.",          defaultDuration:"PT30M",   isLocationOnline:true, staffMemberIds:["staff-sm","staff-ap"]             },
  { id:"svc-002", displayName:"Product Demo",           description:"Live walkthrough of Mekorma inside your ERP. Bring your team — we'll tailor it to your workflow.",    defaultDuration:"PT1H",    isLocationOnline:true, staffMemberIds:["staff-sm","staff-jt","staff-ap"]   },
  { id:"svc-003", displayName:"Partner Onboarding",     description:"Structured session covering portal access, deal registration, and co-selling motions.",               defaultDuration:"PT45M",   isLocationOnline:true, staffMemberIds:["staff-ap","staff-jt"]             },
  { id:"svc-004", displayName:"Account Review",         description:"Scheduled check-in to review your configuration, upgrades, and roadmap alignment.",                  defaultDuration:"PT30M",   isLocationOnline:true, staffMemberIds:["staff-sm","staff-cw"]             },
  { id:"svc-005", displayName:"Technical Deep Dive",    description:"Architecture review covering integrations, customisations, and multi-entity scale.",                 defaultDuration:"PT1H30M", isLocationOnline:true, staffMemberIds:["staff-jt","staff-cw"]             },
  { id:"svc-006", displayName:"Internal / POC Session", description:"Internal evaluation or proof-of-concept session for the Mekorma team.",                              defaultDuration:"PT1H",    isLocationOnline:true, staffMemberIds:["staff-jt","staff-ap","staff-cw"]  },
];

const MOCK_STAFF_POOL = [
  { id:"staff-sm", displayName:"Sarah Mitchell", emailAddress:"sarah@mekorma.com", role:"bookingStaff" },
  { id:"staff-jt", displayName:"James Torres",   emailAddress:"james@mekorma.com", role:"bookingStaff" },
  { id:"staff-ap", displayName:"Anika Patel",    emailAddress:"anika@mekorma.com", role:"bookingStaff" },
  { id:"staff-cw", displayName:"Chris Webb",     emailAddress:"chris@mekorma.com", role:"bookingStaff" },
];

// Deterministic busy blocks per staff member
// Format: [hhmm-start, hhmm-end]  (4-char strings to avoid parser ambiguity)
const STAFF_BUSY_MINS = {
  "staff-sm": [[510,540],[660,690],[840,900]],   // 8:30-9:00, 11:00-11:30, 14:00-15:00
  "staff-jt": [[540,600],[780,840],[930,960]],   // 9:00-10:00, 13:00-14:00, 15:30-16:00
  "staff-ap": [[570,630],[720,750],[870,930]],   // 9:30-10:30, 12:00-12:30, 14:30-15:30
  "staff-cw": [[600,630],[690,720],[900,990]],   // 10:00-10:30, 11:30-12:00, 15:00-16:30
};

const minsToTime = m => `${String(Math.floor(m/60)).padStart(2,"0")}:${String(m%60).padStart(2,"0")}`;

function mockAvailabilityResponse(staffIds, date) {
  const ds = date.toISOString().split("T")[0];
  const DAY_START = 480; // 08:00
  const DAY_END   = 1020; // 17:00

  const staffAvailabilityItems = staffIds.map(staffId => {
    const busy = (STAFF_BUSY_MINS[staffId] || []).slice().sort((a,b)=>a[0]-b[0]);
    const segs = [];
    let cur = DAY_START;
    for (const [bs, be] of busy) {
      if (cur < bs) segs.push({ status:"available", startDateTime:{dateTime:`${ds}T${minsToTime(cur)}:00`,timeZone:"UTC"}, endDateTime:{dateTime:`${ds}T${minsToTime(bs)}:00`,timeZone:"UTC"} });
      segs.push({ status:"busy",      startDateTime:{dateTime:`${ds}T${minsToTime(bs)}:00`,timeZone:"UTC"}, endDateTime:{dateTime:`${ds}T${minsToTime(be)}:00`,timeZone:"UTC"} });
      cur = be;
    }
    if (cur < DAY_END) segs.push({ status:"available", startDateTime:{dateTime:`${ds}T${minsToTime(cur)}:00`,timeZone:"UTC"}, endDateTime:{dateTime:`${ds}T${minsToTime(DAY_END)}:00`,timeZone:"UTC"} });
    return { staffId, availabilityItems: segs };
  });

  return { staffAvailabilityItems };
}

// Slot building lives in src/slotBuilder.js (pure, unit-tested).

// Live availability on the Time step: background re-check cadence, and the
// minimum age before a re-check (skips one right after a date was picked).
const LIVE_REFRESH_MS  = 60_000;
const LIVE_MIN_AGE_MS  = 15_000;

// Staff choices offered for a service (src/staffChoice.js — Settings and
// Bookings' allowStaffSelection, enforced by booking-create), plus the legacy
// per-name CONFIG.serviceMeta staffMode, which the settings can override.
const staffChoiceFor = (settings, svc) => {
  const c = resolveStaffChoice(settings, svc, { schedulingPolicy: { allowStaffSelection: svc?.businessAllowStaffSelection } });
  const legacyAny = (CONFIG.serviceMeta[svc?.displayName] || {}).staffMode === "AnyAvailable";
  return { ...c, anyoneOnly: !c.allowSpecific || (legacyAny && c.allowAnyone) };
};

// Staff whose availability decides the slots: the chosen person, else the
// service's staff, else the whole business roster (service without staff).
const availabilityStaffIds = (selectedStaff, staff, allStaff) =>
  selectedStaff && selectedStaff !== "any"
    ? [selectedStaff.id]
    : staff.length ? staff.map(s => s.id) : allStaff.map(s => s.id);

// ═════════════════════════════════════════════════════════════════════════════
// UTILITIES
// ═════════════════════════════════════════════════════════════════════════════
const uuid = () => "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
  const r = Math.random()*16|0; return (c==="x"?r:(r&0x3|0x8)).toString(16);
});
const delay = (ms, val) => new Promise(r => setTimeout(() => r(val), ms));

// ─────────────────────────────────────────────────────────────────────────────
// ATTRIBUTION CAPTURE
//
// Extracts UTM parameters, ad-platform click IDs, and landing context from
// the URL on first load. Persists to sessionStorage so attribution survives
// in-page navigation — if the user returns without params, we keep the original.
//
// Output is structured for direct write to a Dynamics 365 Lead via
// the Dataverse Web API (field names match the D365 custom schema convention).
// ─────────────────────────────────────────────────────────────────────────────
const ATTR_SESSION_KEY = "mkorma_attribution_v1";

// Per-booking-link sessionStorage key. Scoping by linkId means opening a
// different booking link in the same tab starts fresh attribution, while
// navigating around inside the same link still preserves its real UTMs.
const attrSessionKey = (linkId) =>
  linkId ? `${ATTR_SESSION_KEY}::${linkId}` : ATTR_SESSION_KEY;

// GA4-aligned default channel grouping
const inferChannel = (source, medium) => {
  const s = (source || "").toLowerCase();
  const m = (medium || "").toLowerCase();
  if (m === "email")                                                       return "Email";
  if (["cpc","ppc","paid","paid_search","paidsearch"].includes(m))         return "Paid Search";
  if (["display","banner","cpm","remarketing","retargeting"].includes(m))  return "Display";
  if (["paid_social","paidsocial","social_paid"].includes(m))              return "Paid Social";
  if (["social","social-network","social_network"].includes(m))            return "Organic Social";
  if (m === "affiliate")                                                   return "Affiliate";
  if (["referral","link","partner"].includes(m))                           return "Referral";
  if (m === "video")                                                       return "Video";
  if (m === "(none)" || m === "" || s === "direct")                        return "Direct";
  return "Other / Unassigned";
};

// Drop a query parameter from a URL string (used to keep the reschedule
// capability token out of attribution — it would otherwise land on the Lead).
const withoutParam = (href, name) => {
  try { const u = new URL(href); u.searchParams.delete(name); return u.toString(); } catch { return href; }
};

const captureAttribution = (linkId) => {
  const sessionKey = attrSessionKey(linkId);

  // Does the current URL carry any UTM params?
  const currentSearch = new URLSearchParams(window.location.search);
  const hasUtm = ["utm_source","utm_medium","utm_campaign","utm_term","utm_content","utm_id"]
    .some(k => currentSearch.has(k));

  // Return cached attribution ONLY when the URL has no UTM params.
  // Scoped per-linkId so a different booking link starts fresh rather than
  // inheriting UTMs from another link opened earlier in the same tab.
  if (!hasUtm) {
    try {
      const stored = sessionStorage.getItem(sessionKey);
      if (stored) return JSON.parse(stored);
    } catch {}
  }

  try {
    const p   = new URLSearchParams(window.location.search);
    const get = k => p.get(k) || null;

    // ── Standard UTM parameters ───────────────────────────────────────────────
    // Dynamics 365 Lead target fields shown in comments (custom schema)
    const utm_source   = get("utm_source")   || "direct";   // msdyn_utmsource
    const utm_medium   = get("utm_medium")   || "(none)";   // msdyn_utmmedium
    const utm_campaign = get("utm_campaign");               // msdyn_utmcampaign
    const utm_term     = get("utm_term");                   // msdyn_utmterm
    const utm_content  = get("utm_content");               // msdyn_utmcontent
    const utm_id       = get("utm_id");                    // msdyn_utmid  (GA4 campaign ID)

    // ── Ad-platform click IDs (auto-appended by ad networks, no manual setup) ─
    const gclid     = get("gclid");      // Google Ads (search / shopping / display)
    const gbraid    = get("gbraid");     // Google Ads — app conversion (privacy-safe)
    const wbraid    = get("wbraid");     // Google Ads — web conversion (privacy-safe)
    const fbclid    = get("fbclid");     // Meta Ads (Facebook / Instagram)
    const msclkid   = get("msclkid");   // Microsoft Advertising / Bing Ads
    const li_fat_id = get("li_fat_id"); // LinkedIn Ads (first-party)
    const ttclid    = get("ttclid");    // TikTok Ads
    const twclid    = get("twclid");    // Twitter / X Ads
    const dclid     = get("dclid");     // Display & Video 360 (DV360)

    // Human-readable label for the click ID that fired (for the Lead record)
    const clickIdSource = gclid || gbraid || wbraid ? "Google Ads"
      : fbclid    ? "Meta Ads"
      : msclkid   ? "Microsoft Advertising"
      : li_fat_id ? "LinkedIn Ads"
      : ttclid    ? "TikTok Ads"
      : twclid    ? "Twitter / X Ads"
      : dclid     ? "DV360"
      : null;

    const attribution = {
      // UTM params
      utm_source, utm_medium, utm_campaign, utm_term, utm_content, utm_id,
      // Click IDs
      gclid, gbraid, wbraid, fbclid, msclkid, li_fat_id, ttclid, twclid, dclid,
      clickIdSource,
      // Derived
      channel: inferChannel(utm_source, utm_medium),
      // Context — full snapshot for audit
      landingUrl:  withoutParam(window.location.href, "reschedule"),
      referrer:    document.referrer || null,
      rawParams:   (() => { const q = new URLSearchParams(window.location.search); q.delete("reschedule"); return q.toString() || null; })(),
      capturedAt:  new Date().toISOString(),
    };

    try { sessionStorage.setItem(sessionKey, JSON.stringify(attribution)); } catch {}
    return attribution;
  } catch {
    const fallback = { utm_source:"direct", utm_medium:"(none)", channel:"Direct", capturedAt: new Date().toISOString() };
    return fallback;
  }
};
const getTZ = () => {
  try { const zone=Intl.DateTimeFormat().resolvedOptions().timeZone; const abbr=new Date().toLocaleTimeString("en",{timeZoneName:"short"}).split(" ").at(-1); return {zone,abbr}; }
  catch { return {zone:"UTC",abbr:"UTC"}; }
};
const tzAbbr = (zone) => {
  try { return new Date().toLocaleTimeString("en",{timeZone:zone,timeZoneName:"short"}).split(" ").at(-1); }
  catch { return zone; }
};
const TIMEZONE_OPTIONS = [
  { zone:"Pacific/Honolulu",     label:"Hawaii (HST)" },
  { zone:"America/Anchorage",    label:"Alaska (AKST)" },
  { zone:"America/Los_Angeles",  label:"Pacific (PT)" },
  { zone:"America/Phoenix",      label:"Arizona (MST)" },
  { zone:"America/Denver",       label:"Mountain (MT)" },
  { zone:"America/Chicago",      label:"Central (CT)" },
  { zone:"America/New_York",     label:"Eastern (ET)" },
  { zone:"America/Halifax",      label:"Atlantic (AT)" },
  { zone:"America/St_Johns",     label:"Newfoundland (NT)" },
  { zone:"Europe/London",        label:"London (GMT/BST)" },
  { zone:"Europe/Paris",         label:"Central Europe (CET)" },
  { zone:"Europe/Berlin",        label:"Berlin (CET)" },
  { zone:"Europe/Moscow",        label:"Moscow (MSK)" },
  { zone:"Asia/Dubai",           label:"Dubai (GST)" },
  { zone:"Asia/Kolkata",         label:"India (IST)" },
  { zone:"Asia/Shanghai",        label:"China (CST)" },
  { zone:"Asia/Tokyo",           label:"Tokyo (JST)" },
  { zone:"Australia/Sydney",     label:"Sydney (AEST)" },
  { zone:"Australia/Perth",      label:"Perth (AWST)" },
  { zone:"Pacific/Auckland",     label:"New Zealand (NZST)" },
  { zone:"UTC",                  label:"UTC" },
];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS_H = ["Su","Mo","Tu","We","Th","Fr","Sa"];
const isWeekday = d => d.getDay()>0&&d.getDay()<6;
const isPast    = d => { const t=new Date(); t.setHours(0,0,0,0); return d<t; };
const isToday   = d => d.toDateString()===new Date().toDateString();
const fmtDate   = d => d.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"});
const fmtShort  = d => d.toLocaleDateString("en-US",{month:"short",day:"numeric"});
// validateForm lives in src/formValidation.js (pure, unit-tested).

// ═════════════════════════════════════════════════════════════════════════════
// MEKORMA LOGO  —  inline SVG, extracted from mekorma_onlight.svg
// Brand colours: #004e42 (primary) · #88c96c (lime accent)
// ═════════════════════════════════════════════════════════════════════════════
export function MekormaLogo({ height = 28 }) {
  const w = Math.round(height * (7855 / 857));
  return (
    <svg width={w} height={height} viewBox="0 0 7855 857"
         xmlns="http://www.w3.org/2000/svg"
         style={{ fillRule:"evenodd", clipRule:"evenodd", display:"block" }}>
      {/* Letterforms — brand primary */}
      <path d="M1486.78,705.813l0,-226.656l396.875,-0l0,-125.625l-396.875,-0l0,-213.625l447.344,-0l0,-128.125l-600,-0l0,822.125l616.531,-0l0,-128.125l-463.875,0.031Z" fill="#004e42"/>
      <rect x="2394.59" y="11.813" width="152.531" height="822.094" fill="#004e42"/>
      <path d="M5195.03,563.657c56.417,-21.771 99.729,-55.031 129.938,-99.781c30.208,-44.75 45.271,-97.959 45.187,-159.625c0,-60.271 -14.114,-112.355 -42.343,-156.25c-28.23,-43.896 -68.761,-77.563 -121.594,-101c-52.709,-23.417 -114.75,-35.157 -186.125,-35.219l-338.219,-0l0,822.125l152.719,-0l-0,-239.688l185.5,0c10.604,0.189 21.211,-0.155 31.781,-1.031l168,240.625l164.25,0l-189.094,-270.156Zm-30.343,-137.844c-34.626,28.458 -85.126,42.688 -151.501,42.688l-178.593,-0l-0,-327.594l178.593,-0c66.376,-0 116.876,14.281 151.501,42.375c34.624,28.094 51.531,68.406 51.531,120.969c-0,52.562 -17.125,93.062 -51.532,121.562Z" fill="#004e42"/>
      <path d="M7482.91,11.813l-150.281,0l-369.813,822.094l157.219,-0l81.031,-190.219l411.188,0l82.063,190.219l159.843,-0l-371.25,-822.094Zm-230.031,511.938l153.75,-359.219l155.187,359.375l-308.937,-0.156Z" fill="#004e42"/>
      <path d="M822.125,11.751l0,822.125l-145.719,-0l0,-594.063l-265.25,252.688l-265.437,-252.688l-0,594.063l-145.719,-0l0,-822.125l112.938,-0l298.218,287.125l298.032,-287.125l112.937,-0Z" fill="#004e42"/>
      <path d="M6602.91,11.751l0,822.125l-145.687,-0l-0,-594.063l-265.282,252.688l-265.406,-252.688l0,594.063l-145.75,-0l0,-822.125l112.938,-0l298.218,287.125l298.063,-287.125l112.906,-0Z" fill="#004e42"/>
      <path d="M2753.03,422.938l411.156,410.969l-205.999,-0l-411.157,-410.969l411.157,-411.156l205.999,-0l-411.156,411.156Z" fill="#004e42"/>
      <path d="M4249.34,205.688c-37.956,-63.941 -92.775,-116.23 -158.438,-151.125c-67.416,-36.25 -143.031,-54.437 -226.843,-54.562c-83.813,-0.125 -159.365,18.062 -226.657,54.562c-65.725,34.993 -120.552,87.43 -158.437,151.531c-38.459,64.75 -57.667,136.969 -57.625,216.657c-0,80.041 19.208,152.27 57.625,216.687c13.078,22.195 28.285,43.064 45.406,62.313l104.875,-104.875c-6.074,-8.104 -11.587,-16.613 -16.5,-25.469c-25,-44.188 -37.5,-93.74 -37.5,-148.656c0,-54.917 12.5,-104.396 37.5,-148.438c24.253,-43.392 60.077,-79.215 103.469,-103.469c43.75,-24.625 93.031,-36.906 147.843,-36.843c54.813,0.062 104.209,12.343 148.188,36.843c43.345,24.315 79.154,60.124 103.469,103.469c25,44.188 37.5,93.667 37.5,148.438c-0,54.77 -12.5,104.323 -37.5,148.656c-6.371,11.348 -13.524,22.238 -21.407,32.594l104.282,104.25c19.313,-20.893 36.375,-43.758 50.906,-68.219c38.292,-64.146 57.5,-136.573 57.625,-217.281c0.125,-80.709 -19.135,-153.063 -57.781,-217.063Z" fill="#004e42"/>
      {/* Lime accent — the triangular signature mark on the O */}
      <path d="M4169.41,856.282l-610.5,-0l305.25,-305.25l144.375,144.406l111.657,111.625l49.218,49.219Z" fill="#88c96c"/>
    </svg>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// LOADING SCREENS  —  shown on initial booking-link load until the wizard is
// ready. Hides the "fetching services…" spinner and any would-be-skipped step
// flashes behind a branded, paced experience.
// ═════════════════════════════════════════════════════════════════════════════
const LOADING_MESSAGES = [
  "Resolving your booking link…",
  "Loading available sessions…",
  "Preparing your experience…",
  "Almost ready…",
];

function LoadingScreen({ message }) {
  return (
    <div style={{ minHeight:"100vh", background:"#F5FAFA", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"-apple-system,'Segoe UI',system-ui,sans-serif", padding:"32px" }}>
      <style>{`
        @keyframes brandSpin { to { transform: rotate(360deg); } }
        @keyframes brandSpinRev { to { transform: rotate(-360deg); } }
        @keyframes msgSwap { 0% { opacity: 0; transform: translateY(4px); } 100% { opacity: 1; transform: none; } }
        @keyframes fadeUpSlow { 0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: none; } }
      `}</style>
      <div style={{ textAlign:"center", maxWidth:380, animation:"fadeUpSlow .4s ease both" }}>
        <MekormaLogo height={30}/>

        {/* Dual-ring spinner — outer arc in primary, inner in accent, rotating in opposite directions */}
        <div style={{ position:"relative", width:72, height:72, margin:"44px auto 28px" }}>
          <svg width="72" height="72" viewBox="0 0 72 72" style={{ position:"absolute", inset:0, animation:"brandSpin 1.6s linear infinite" }}>
            <circle cx="36" cy="36" r="30" fill="none" stroke="#E2E8F0" strokeWidth="3"/>
            <circle cx="36" cy="36" r="30" fill="none" stroke="#004e42" strokeWidth="3" strokeLinecap="round" strokeDasharray="55 200"/>
          </svg>
          <svg width="72" height="72" viewBox="0 0 72 72" style={{ position:"absolute", inset:0, animation:"brandSpinRev 2.4s linear infinite" }}>
            <circle cx="36" cy="36" r="20" fill="none" stroke="#88c96c" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="30 120"/>
          </svg>
        </div>

        <h2 style={{ fontSize:17, fontWeight:700, color:"#0F172A", margin:"0 0 6px", letterSpacing:"0.01em" }}>Getting things ready</h2>
        <p
          key={message}
          style={{ fontSize:13, color:"#64748B", margin:0, minHeight:20, animation:"msgSwap .3s ease both" }}
        >
          {message}
        </p>
      </div>
    </div>
  );
}

function LoadingTimeoutScreen() {
  return (
    <div style={{ minHeight:"100vh", background:"#F5FAFA", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"-apple-system,'Segoe UI',system-ui,sans-serif", padding:"32px" }}>
      <div style={{ background:"#fff", border:"1.5px solid #E2E8F0", borderRadius:16, padding:"40px 44px", textAlign:"center", maxWidth:420, width:"100%" }}>
        <MekormaLogo height={26}/>
        <div style={{ width:52, height:52, borderRadius:"50%", background:"#FEF3C7", display:"flex", alignItems:"center", justifyContent:"center", margin:"28px auto 18px" }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#D97706" strokeWidth="1.8"/><path d="M12 7v5l3 2" stroke="#D97706" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        <h2 style={{ fontSize:18, fontWeight:700, color:"#0F172A", margin:"0 0 8px" }}>This is taking longer than usual</h2>
        <p style={{ fontSize:13, color:"#64748B", margin:"0 0 22px", lineHeight:1.6 }}>
          We couldn&apos;t finish loading your booking in time. Please try reloading the page.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{ padding:"11px 26px", borderRadius:10, border:"none", background:"#004e42", color:"#fff", fontWeight:600, fontSize:14, cursor:"pointer" }}
        >
          Reload
        </button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ICONS
// ═════════════════════════════════════════════════════════════════════════════
const Ic = {
  Check:  ({s=16,c="currentColor"}) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M2.5 8.5l3.5 3.5 7.5-8" stroke={c} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  ChevL:  ({s=16}) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M10 3L6 8l4 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  ChevR:  ({s=16}) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M6 3l4 5-4 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  Clock:  ({s=13}) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3"/><path d="M8 5v3l2 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>,
  Globe:  ({s=13}) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3"/><ellipse cx="8" cy="8" rx="3" ry="6.5" stroke="currentColor" strokeWidth="1.3"/><path d="M1.5 8h13" stroke="currentColor" strokeWidth="1.3"/></svg>,
  Loader: ({s=18}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" style={{animation:"spin .75s linear infinite"}}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeDasharray="48" strokeDashoffset="20" strokeLinecap="round"/></svg>,
  Alert:  ({s=14}) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3"/><path d="M8 5v4M8 11v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  MS:     ({s=11}) => <svg width={s} height={s} viewBox="0 0 16 16"><rect x="1" y="1" width="6.5" height="6.5" fill="#F25022"/><rect x="8.5" y="1" width="6.5" height="6.5" fill="#7FBA00"/><rect x="1" y="8.5" width="6.5" height="6.5" fill="#00A4EF"/><rect x="8.5" y="8.5" width="6.5" height="6.5" fill="#FFB900"/></svg>,
  Calendar: ({s=16,c="currentColor"}) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="12" height="11" rx="1.5" stroke={c} strokeWidth="1.3"/><path d="M2 6.5h12" stroke={c} strokeWidth="1.3"/><path d="M5.5 1.5v3M10.5 1.5v3" stroke={c} strokeWidth="1.3" strokeLinecap="round"/></svg>,
  XCircle: ({s=16,c="currentColor"}) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke={c} strokeWidth="1.3"/><path d="M5.75 5.75l4.5 4.5M10.25 5.75l-4.5 4.5" stroke={c} strokeWidth="1.3" strokeLinecap="round"/></svg>,
  Plus: ({s=16,c="currentColor"}) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>,
};

// ═════════════════════════════════════════════════════════════════════════════
// SHARED STYLES
// ═════════════════════════════════════════════════════════════════════════════
const S = {
  card:    { background:"#fff", border:"1.5px solid #E2E8F0", borderRadius:12, padding:"18px 20px" },
  cardSel: a => ({ background:"#F5FAFA", border:`2px solid ${a}`, borderRadius:12, padding:"17px 19px" }),
  btn: (v="primary", off=false) => ({
    display:"inline-flex", alignItems:"center", justifyContent:"center", gap:8,
    padding: v==="primary" ? "11px 24px" : "10px 20px", borderRadius:10, fontWeight:600,
    fontSize:14, cursor:off?"not-allowed":"pointer",
    border: v==="primary" ? "none" : "1.5px solid #E2E8F0",
    background: v==="primary" ? (off?"#7aaa9e":"#004e42") : "#fff",
    color: v==="primary" ? "#fff" : (off?"#CBD5E1":"#374151"), transition:"all .15s",
  }),
  tag: (c,b) => ({ fontSize:11, fontWeight:600, color:c, background:b, padding:"2px 8px", borderRadius:20, display:"inline-flex", alignItems:"center", gap:4 }),
  label: { fontSize:12, fontWeight:600, color:"#64748B", letterSpacing:"0.04em", textTransform:"uppercase", marginBottom:6, display:"block" },
  input: { width:"100%", padding:"10px 13px", borderRadius:8, border:"1.5px solid #E2E8F0", fontSize:14, color:"#0F172A", outline:"none", transition:"border .15s", background:"#fff", boxSizing:"border-box" },
  h2:    { fontSize:22, fontWeight:700, color:"#0F172A", marginBottom:6, lineHeight:1.3 },
  sub:   { fontSize:14, color:"#64748B", marginBottom:22, lineHeight:1.6 },
};

const GraphBadge = ({ label }) => (
  <span style={{ ...S.tag("#0078D4","#EFF6FF"), fontSize:10 }}><Ic.MS s={11}/> {label}</span>
);

function Skeleton({ height=52 }) {
  return <div style={{ height, borderRadius:10, background:"#F1F5F9", animation:"pulse 1.4s ease-in-out infinite" }}/>;
}

// ═════════════════════════════════════════════════════════════════════════════
// RELATIONSHIP TYPE ICONS  —  purposeful SVG, one per audience type
// ═════════════════════════════════════════════════════════════════════════════
const RT_ICONS = {
  // New Prospect — person discovering / first contact (person + magnifying glass)
  prospect: ({ c = "currentColor" }) => (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
      <circle cx="9.5" cy="7.5" r="4" stroke={c} strokeWidth="1.65"/>
      <path d="M2.5 20c0-3.6 3.1-6.5 7-6.5" stroke={c} strokeWidth="1.65" strokeLinecap="round"/>
      <circle cx="18" cy="18" r="3.25" stroke={c} strokeWidth="1.65"/>
      <path d="M20.3 20.3l2.2 2.2" stroke={c} strokeWidth="1.65" strokeLinecap="round"/>
    </svg>
  ),

  // Reseller / Partner — two people side-by-side (collaboration)
  partner: ({ c = "currentColor" }) => (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
      <circle cx="8.5" cy="7" r="3.5" stroke={c} strokeWidth="1.65"/>
      <path d="M2 19.5c0-3.3 2.9-6 6.5-6" stroke={c} strokeWidth="1.65" strokeLinecap="round"/>
      <circle cx="16.5" cy="7" r="3.5" stroke={c} strokeWidth="1.65"/>
      <path d="M22 19.5c0-3.3-2.9-6-6.5-6" stroke={c} strokeWidth="1.65" strokeLinecap="round"/>
      <path d="M12 13.8v-.3" stroke={c} strokeWidth="1.65" strokeLinecap="round"/>
    </svg>
  ),

  // Existing Customer — building / established company (verified relationship)
  existing: ({ c = "currentColor" }) => (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
      <path d="M3 21h18" stroke={c} strokeWidth="1.65" strokeLinecap="round"/>
      <path d="M5 21V8l7-5 7 5v13" stroke={c} strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="9" y="13" width="6" height="8" rx="1" stroke={c} strokeWidth="1.5"/>
      <rect x="7" y="10" width="3" height="3" rx=".5" stroke={c} strokeWidth="1.4"/>
      <rect x="14" y="10" width="3" height="3" rx=".5" stroke={c} strokeWidth="1.4"/>
    </svg>
  ),

  // Internal / Demo — laptop screen (demos, internal evaluations, POC)
  internal: ({ c = "currentColor" }) => (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
      <rect x="2" y="4.5" width="20" height="13" rx="2" stroke={c} strokeWidth="1.65"/>
      <path d="M8 21.5h8" stroke={c} strokeWidth="1.65" strokeLinecap="round"/>
      <path d="M12 17.5v4" stroke={c} strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M8.5 10.5l2.5 2.5 4.5-5" stroke={c} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
};

// ═════════════════════════════════════════════════════════════════════════════
// STEP 0 — Relationship Type
// ═════════════════════════════════════════════════════════════════════════════
// Map a Dataverse OptionSet label to one of the built-in RT_ICONS via keywords.
function iconKeyFromLabel(label) {
  const s = (label || "").toLowerCase();
  if (s.includes("partner") || s.includes("reseller") || s.includes("var") || s.includes("isv")) return "partner";
  if (s.includes("existing") || s.includes("customer") || s.includes("client")) return "existing";
  if (s.includes("internal") || s.includes("demo") || s.includes("poc") || s.includes("staff")) return "internal";
  return "prospect";
}

// Fallback short descriptions when the OptionSet has no Description set.
function fallbackDescription(label) {
  const s = (label || "").toLowerCase();
  if (s === "end-user" || s === "end user") return "Customer using Mekorma in their own environment";
  if (s === "partner") return "VAR, ISV, or implementation partner";
  if (s === "reseller" || s.includes("reseller")) return "Reselling Mekorma to end customers";
  if (s === "prospect (end-user)" || s === "prospect (end user)") return "Evaluating Mekorma for their own use";
  if (s === "prospect (partner)") return "Exploring a partnership with Mekorma";
  if (s.startsWith("prospect")) return "Evaluating Mekorma for the first time";
  if (s === "vendor") return "Supplier or third-party vendor";
  if (s === "spla") return "Service Provider License Agreement account";
  if (s === "merged") return "Record consolidated from a previous lead";
  if (s === "other") return "Doesn't fit the other categories";
  if (s.includes("existing") || s.includes("customer")) return "Already running Mekorma in production";
  if (s.includes("internal") || s.includes("demo")) return "Mekorma team or internal evaluation";
  return "Schedule time with our team";
}

function RelationshipStep({ options, loading, error, selected, onSelect }) {
  if (loading) return (
    <div>
      <h2 style={S.h2}>What best describes you?</h2>
      <p style={S.sub}>Loading options…</p>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
        {[1,2,3,4].map(i => <Skeleton key={i} height={82}/>)}
      </div>
    </div>
  );

  if (error || !options?.length) return (
    <div>
      <h2 style={S.h2}>What best describes you?</h2>
      <div style={{ ...S.card, background:"#FFF1F2", borderColor:"#FCA5A5", display:"flex", gap:10, alignItems:"center" }}>
        <Ic.Alert s={18}/>
        <span style={{ fontSize:13, color:"#991B1B" }}>
          {error ? `Could not load relationship types: ${error}` : "No relationship types are configured on the Lead entity."}
        </span>
      </div>
    </div>
  );

  return (
    <div>
      <h2 style={S.h2}>What best describes you?</h2>
      <p style={S.sub}>This determines which Bookings services are presented to you.</p>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
        {options.map(rt => {
          const sel = selected?.value === rt.value;
          const Icon = RT_ICONS[iconKeyFromLabel(rt.label)];
          return (
            <button key={rt.value} onClick={() => onSelect(rt)} style={{
              ...(sel ? S.cardSel("#004e42") : S.card),
              cursor:"pointer", textAlign:"left", display:"flex",
              flexDirection:"row", alignItems:"flex-start", gap:14, transition:"all .15s",
            }}>
              <div style={{
                width:44, height:44, borderRadius:10,
                background: sel ? "#004e42" : "#E8F2EF",
                display:"flex", alignItems:"center", justifyContent:"center",
                transition:"background .15s", flexShrink:0,
              }}>
                {Icon && <Icon c={sel ? "#fff" : "#004e42"}/>}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:700, color:sel?"#003a31":"#0F172A", marginBottom:4 }}>
                  {rt.label}
                </div>
                <div style={{ fontSize:12, color:"#94A3B8", lineHeight:1.5 }}>
                  {rt.description || fallbackDescription(rt.label)}
                </div>
                {sel && <span style={{ ...S.tag("#003a31","#C5DDD9"), marginTop:8, display:"inline-block" }}>Selected ✓</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// STEP 1 — Services (live from Graph)
// ═════════════════════════════════════════════════════════════════════════════
function ServiceStep({ relType, services, loading, error, selected, onSelect, allStaff, staffPhotos, appSettings }) {
  // Show all services from Graph — filtering by relationship type will be added later.
  const visible = services;
  console.log(`[ServiceStep] relType=${relType?.label}, visible=${visible.length}/${services.length}`);

  if (loading) return (
    <div>
      <h2 style={S.h2}>Choose a session</h2>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:20 }}>
        <Ic.Loader s={14}/>
        <span style={{ fontSize:13, color:"#64748B" }}>Fetching services from Microsoft Bookings…</span>
      </div>
      {[1,2,3].map(i => <div key={i} style={{ marginBottom:9 }}><Skeleton height={72}/></div>)}
    </div>
  );

  if (error) return (
    <div>
      <h2 style={S.h2}>Choose a session</h2>
      <div style={{ ...S.card, background:"#FFF1F2", borderColor:"#FCA5A5", display:"flex", gap:10, alignItems:"center" }}>
        <Ic.Alert s={18}/><span style={{ fontSize:13, color:"#991B1B" }}>Could not load services: {error}</span>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
        <h2 style={{ ...S.h2, marginBottom:0 }}>Choose a session</h2>
        <GraphBadge label={`${visible.length} sessions available`}/>
      </div>
      <p style={S.sub}>{relType?.label ? <>Sessions available for <strong style={{color:"#0F172A"}}>{relType.label}</strong>.</> : <>Pick a session to continue.</>}</p>
      <div style={{ maxHeight:"60vh", overflowY:"auto", paddingRight:14 }}>
        <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
          {visible.map(svc => {
            const meta = CONFIG.serviceMeta[svc.displayName] || {};
            const accent = meta.accent || "#004e42";
            const dur = parseDuration(svc.defaultDuration);
            const sel = selected?.id === svc.id;
            return (
              <button key={svc.id} onClick={() => onSelect(svc)} style={{ ...(sel?S.cardSel(accent):S.card), cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:14, transition:"all .15s", width:"100%" }}>
                <div style={{ width:4, minHeight:52, borderRadius:4, background:accent, flexShrink:0 }}/>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:4, flexWrap:"wrap" }}>
                    <span style={{ fontSize:14, fontWeight:700, color:"#0F172A" }}>{svc.displayName}</span>
                    <span style={{ ...S.tag("#64748B","#F1F5F9"), display:"inline-flex", alignItems:"center", gap:3 }}><Ic.Clock s={11}/>{dur} min</span>
                    {staffChoiceFor(appSettings, svc).anyoneOnly && <span style={S.tag("#059669","#ECFDF5")}>Any staff</span>}
                    {svc.isLocationOnline && <span style={S.tag("#0078D4","#EFF6FF")}>Online</span>}
                  </div>
                  <span style={{ fontSize:12, color:"#94A3B8", lineHeight:1.5 }}>{svc.description}</span>
                  {(() => {
                    const ids = svc.staffMemberIds || [];
                    const assigned = allStaff.filter(s => ids.includes(s.id));
                    if (!assigned.length) return (
                      <div style={{ fontSize:11, color:"#CBD5E1", marginTop:5 }}>No staff assigned</div>
                    );
                    return (
                      <div style={{ display:"flex", alignItems:"center", gap:0, marginTop:6 }}>
                        {assigned.slice(0, 4).map((p, i) => (
                          <div key={p.id} style={{ marginLeft: i === 0 ? 0 : -8, zIndex: assigned.length - i, position:"relative" }} title={p.displayName}>
                            <StaffAvatar person={p} photos={staffPhotos} size={24} border="#fff" />
                          </div>
                        ))}
                        {assigned.length > 4 && (
                          <div style={{ marginLeft:-6, width:24, height:24, borderRadius:"50%", background:"#F1F5F9", border:"2px solid #fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, color:"#64748B", zIndex:0 }}>
                            +{assigned.length - 4}
                          </div>
                        )}
                        <span style={{ fontSize:11, color:"#CBD5E1", marginLeft:8 }}>{assigned.length} staff</span>
                      </div>
                    );
                  })()}
                </div>
                {sel && <Ic.Check s={18} c={accent}/>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// STEP 2 — Staff (from service.staffMemberIds via Graph)
// ═════════════════════════════════════════════════════════════════════════════
// ── Staff avatar: photo with fallback to initials ────────────────────────────
function StaffAvatar({ person, photos = {}, size = 48, border }) {
  const em = CONFIG.staffMeta[person.emailAddress?.toLowerCase()] || {};
  const initials = em.initials || person.displayName.split(" ").map(w => w[0]).join("").slice(0, 2);
  const photoUrl = photos[person.emailAddress] || photos[person.emailAddress?.toLowerCase()];
  const borderStyle = border ? { border: `2px solid ${border}`, boxSizing: "content-box" } : {};

  if (photoUrl) {
    return (
      <img src={photoUrl} alt={person.displayName}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0, ...borderStyle }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: em.avatarBg || "#F1F5F9",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.27, fontWeight: 700, color: em.avatarFg || "#334155", flexShrink: 0,
      ...borderStyle,
    }}>
      {initials}
    </div>
  );
}

function StaffStep({ service, staff, loadingStaff, staffError, selected, onSelect, staffPhotos = {}, choice = { allowAnyone: true, anyoneOnly: false }, notice = null }) {
  const staffMode = choice.anyoneOnly ? "AnyAvailable" : "UserSelectable";
  const noticeBox = notice && (
    <div role="alert" style={{ background:"#FFFBEB", border:"1px solid #FCD34D", borderRadius:10, padding:"10px 12px", marginBottom:14, fontSize:12, color:"#92400E", lineHeight:1.5 }}>{notice}</div>
  );

  if (loadingStaff) return (
    <div>
      <h2 style={S.h2}>Select a team member</h2>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:20 }}>
        <Ic.Loader s={14}/>
        <span style={{ fontSize:13, color:"#64748B" }}>Fetching staff assigned to this service…</span>
      </div>
      {[1,2].map(i => <div key={i} style={{ marginBottom:9 }}><Skeleton height={86}/></div>)}
    </div>
  );

  if (staffError) return (
    <div>
      <h2 style={S.h2}>Select a team member</h2>
      <div style={{ ...S.card, background:"#FFF1F2", borderColor:"#FCA5A5", display:"flex", gap:10, alignItems:"center" }}>
        <Ic.Alert s={18}/><span style={{ fontSize:13, color:"#991B1B" }}>{staffError}</span>
      </div>
    </div>
  );

  // AnyAvailable: skip picker, show roster only for transparency
  if (staffMode === "AnyAvailable") return (
    <div>
      {noticeBox}
      <h2 style={S.h2}>Team assignment</h2>
      <p style={S.sub}>
        <strong style={{color:"#0F172A"}}>{service?.displayName}</strong> auto-assigns to the next available team member.
      </p>
      <div style={{ ...S.card, background:"#F4FAF8", borderColor:"#B3CECA", display:"flex", alignItems:"center", gap:14, marginBottom:18 }}>
        <div style={{ width:48, height:48, borderRadius:"50%", background:"#E8F2EF", display:"flex", alignItems:"center", justifyContent:"center", color:"#004e42" }}><svg width={20} height={20} viewBox="0 0 24 24" fill="none"><circle cx="9" cy="7" r="3.5" stroke="currentColor" strokeWidth="1.6"/><path d="M2.5 19.5c0-3.3 2.7-6 6.5-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><circle cx="16" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M21.5 19.5c0-2.5-2.2-4.5-5.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg></div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:14, fontWeight:700, color:"#0F172A", marginBottom:2 }}>Next available team member</div>
          <div style={{ fontSize:12, color:"#64748B" }}>Confirmed in the invitation email.</div>
        </div>
        <Ic.Check s={18} c="#004e42"/>
      </div>
      <div style={{ fontSize:11, fontWeight:700, color:"#94A3B8", letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:8 }}>
        Eligible team members ({staff.length})
      </div>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
        {staff.map(p => (
          <div key={p.id} style={{ display:"flex", alignItems:"center", gap:8, ...S.card, padding:"8px 12px" }}>
            <StaffAvatar person={p} photos={staffPhotos} size={28} />
            <span style={{ fontSize:12, color:"#0F172A", fontWeight:600 }}>{p.displayName}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // UserSelectable: full picker
  return (
    <div>
      {noticeBox}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
        <h2 style={{ ...S.h2, marginBottom:0 }}>Select a team member</h2>
        <GraphBadge label={`${staff.length} team member${staff.length !== 1 ? "s" : ""}`}/>
      </div>
      <p style={S.sub}>
        Choose who you'd like to meet with for your <strong style={{color:"#0F172A"}}>{service?.displayName}</strong> session.
      </p>

      {/* Any available (hidden by Settings → Availability → Anyone available) */}
      {choice.allowAnyone && <button onClick={() => onSelect(null)} style={{ ...(selected==="any"?S.cardSel("#004e42"):S.card), cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:14, marginBottom:9, width:"100%", transition:"all .15s" }}>
        <div style={{ width:48, height:48, borderRadius:"50%", background:"#F1F5F9", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, color:"#64748B" }}><svg width={20} height={20} viewBox="0 0 24 24" fill="none"><circle cx="9" cy="7" r="3.5" stroke="currentColor" strokeWidth="1.6"/><path d="M2.5 19.5c0-3.3 2.7-6 6.5-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><circle cx="16" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M21.5 19.5c0-2.5-2.2-4.5-5.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg></div>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:selected==="any"?"#003a31":"#0F172A" }}>Any available</div>
          <div style={{ fontSize:12, color:"#94A3B8" }}>We'll match you with a team member who's free</div>
        </div>
        {selected==="any" && <div style={{marginLeft:"auto"}}><Ic.Check s={18} c="#004e42"/></div>}
      </button>}

      <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
        {staff.map(person => {
          const em = CONFIG.staffMeta[person.emailAddress?.toLowerCase()] || {};
          const sel = selected?.id === person.id;
          return (
            <button key={person.id} onClick={() => onSelect(person)} style={{ ...(sel?S.cardSel("#004e42"):S.card), cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:14, transition:"all .15s", width:"100%" }}>
              <StaffAvatar person={person} photos={staffPhotos} size={48} />
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontWeight:700, color:sel?"#003a31":"#0F172A", marginBottom:2 }}>{person.displayName}</div>
                <div style={{ fontSize:12, color:"#64748B", marginBottom:em.bio?4:0 }}>{em.title || person.role}</div>
                {em.bio && <div style={{ fontSize:12, color:"#94A3B8" }}>{em.bio}</div>}
              </div>
              {sel && <Ic.Check s={18} c="#004e42"/>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// STEP 3 — Calendar + Time Slots (availability from Graph)
// ═════════════════════════════════════════════════════════════════════════════
function CalendarStep({ service, staff, selectedStaff, selectedDate, selectedSlot, onDateSelect, onSlotSelect, slotNotice, loadingSlots, slots, slotsError, serviceDays, serviceDaysLoading, onMonthChange, tz, onRefresh, onTimezoneChange, allStaff, staffPhotos, showTimezonePicker = true }) {
  const today = new Date();
  const [cal, setCal] = useState(() => {
    const d = selectedDate || today;
    return { year:d.getFullYear(), month:d.getMonth() };
  });
  const dur = parseDuration(service?.defaultDuration);

  // Follow the selected date (e.g. auto-moved to the service's first open
  // date) and tell the parent which month is visible so it can load it.
  useEffect(() => {
    if (selectedDate) setCal({ year:selectedDate.getFullYear(), month:selectedDate.getMonth() });
  }, [selectedDate?.toDateString()]);
  useEffect(() => { onMonthChange?.(cal); }, [cal.year, cal.month]);

  // A date is selectable when the service policy says it's open. Unknown
  // (still loading) ⇒ not selectable yet. Policy unavailable (serviceDays
  // null, not loading) ⇒ legacy weekday rule so the page still works.
  const isUnavailable = (d) => {
    if (isPast(d)) return true;
    if (serviceDays) return !serviceDays[ymdLocal(d)]?.open;
    if (serviceDaysLoading) return true;
    return !isWeekday(d);
  };

  const daysInMonth = new Date(cal.year, cal.month+1, 0).getDate();
  const firstDay    = new Date(cal.year, cal.month, 1).getDay();
  const prevMonth   = () => setCal(c => c.month===0?{year:c.year-1,month:11}:{...c,month:c.month-1});
  const nextMonth   = () => setCal(c => c.month===11?{year:c.year+1,month:0}:{...c,month:c.month+1});

  const morning   = slots.filter(s => s.period === "Morning");
  const afternoon = slots.filter(s => s.period === "Afternoon");

  const staffLabel = selectedStaff && selectedStaff !== "any"
    ? selectedStaff.displayName
    : staff.length > 0 ? `Any of ${staff.length} staff` : "Any available";

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
        <h2 style={{ ...S.h2, marginBottom:0 }}>Pick a date and time</h2>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          {selectedDate && (
            <button onClick={onRefresh} disabled={loadingSlots} title="Refresh availability" style={{
              background:"none", border:"1.5px solid #E2E8F0", borderRadius:7, width:28, height:28,
              cursor: loadingSlots ? "default" : "pointer", display:"flex", alignItems:"center", justifyContent:"center",
              opacity: loadingSlots ? 0.5 : 1, transition:"all .15s",
            }}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: loadingSlots ? "spin 1s linear infinite" : "none" }}>
                <path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
              </svg>
            </button>
          )}
          <GraphBadge label="Live availability"/>
        </div>
      </div>
      <p style={S.sub}>
        <strong style={{color:"#0F172A"}}>{service?.displayName}</strong> · {dur} min · {staffLabel}
      </p>

      <div className="calendar-layout" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, alignItems:"start" }}>

        {/* ── Calendar panel ── */}
        <div style={{ ...S.card, padding:14 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
            <button onClick={prevMonth} style={{ background:"none", border:"1px solid #E2E8F0", borderRadius:7, width:28, height:28, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><Ic.ChevL s={13}/></button>
            <span style={{ fontSize:13, fontWeight:700, color:"#0F172A" }}>{MONTHS[cal.month]} {cal.year}</span>
            <button onClick={nextMonth} style={{ background:"none", border:"1px solid #E2E8F0", borderRadius:7, width:28, height:28, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><Ic.ChevR s={13}/></button>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, marginBottom:3 }}>
            {DAYS_H.map(d => <div key={d} style={{ textAlign:"center", fontSize:10, fontWeight:700, color:"#94A3B8", padding:"3px 0" }}>{d}</div>)}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
            {Array.from({length:firstDay}).map((_,i) => <div key={`e${i}`}/>)}
            {Array.from({length:daysInMonth}).map((_,i) => {
              const d = new Date(cal.year, cal.month, i+1);
              const unavail = isUnavailable(d);
              const sel = selectedDate?.toDateString() === d.toDateString();
              const tod = isToday(d);
              return (
                <button key={i} disabled={unavail} onClick={() => !unavail && onDateSelect(d)} style={{
                  width:"100%", aspectRatio:"1", display:"flex", alignItems:"center", justifyContent:"center",
                  borderRadius:7, border: tod&&!sel ? "1.5px solid #004e42" : "1.5px solid transparent",
                  background: sel ? "#004e42" : "transparent",
                  color: unavail ? "#CBD5E1" : sel ? "#fff" : tod ? "#004e42" : "#0F172A",
                  fontSize:12, fontWeight: sel||tod ? 700 : 400,
                  cursor: unavail ? "default" : "pointer", transition:"all .1s",
                }}>
                  {i+1}
                </button>
              );
            })}
          </div>

          <div style={{ marginTop:10, display:"flex", gap:10, flexWrap:"wrap" }}>
            {[
              [{width:10,height:10,borderRadius:3,border:"1.5px solid #004e42"},"Today"],
              [{width:10,height:10,borderRadius:3,background:"#004e42"},"Selected"],
              [{width:10,height:10,borderRadius:3,background:"#F1F5F9"},"Unavailable"],
            ].map(([box,txt],i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:5 }}><div style={box}/><span style={{ fontSize:10, color:"#94A3B8" }}>{txt}</span></div>
            ))}
          </div>

          {/* ── Timezone picker ── */}
          {showTimezonePicker && (
          <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #F1F5F9" }}>
            <label style={{ fontSize:10, fontWeight:700, color:"#94A3B8", letterSpacing:"0.05em", textTransform:"uppercase", display:"block", marginBottom:4 }}>Timezone</label>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <Ic.Globe s={13}/>
              <select value={tz.zone} onChange={e => onTimezoneChange(e.target.value)} style={{
                flex:1, fontSize:12, color:"#0F172A", fontWeight:600,
                padding:"6px 8px", borderRadius:7, border:"1.5px solid #E2E8F0",
                background:"#fff", cursor:"pointer", appearance:"auto",
              }}>
                {/* Show current timezone first if not in the list */}
                {!TIMEZONE_OPTIONS.some(t => t.zone === tz.zone) && (
                  <option value={tz.zone}>{tz.zone} ({tz.abbr})</option>
                )}
                {TIMEZONE_OPTIONS.map(t => (
                  <option key={t.zone} value={t.zone}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>
          )}
        </div>

        {/* ── Slot panel ── */}
        <div>
          {slotNotice && (
            <div style={{ ...S.card, background:"#FFFBEB", borderColor:"#FCD34D", display:"flex", gap:10, alignItems:"center", marginBottom:10 }}>
              <Ic.Alert s={16}/><span style={{ fontSize:12, color:"#92400E" }}>{slotNotice}</span>
            </div>
          )}
          {!selectedDate && (
            <div style={{ ...S.card, textAlign:"center", padding:"38px 16px", background:"#FAFAFA" }}>
              <div style={{ fontSize:26, marginBottom:8 }}>◷</div>
              <div style={{ fontSize:12, color:"#94A3B8" }}>Select a date to see available times</div>
            </div>
          )}

          {selectedDate && loadingSlots && (
            <div style={{ ...S.card, textAlign:"center", padding:"38px 16px" }}>
              <Ic.Loader s={22}/>
              <div style={{ fontSize:12, color:"#64748B", marginTop:10 }}>Loading available times…</div>
              <div style={{ fontSize:11, color:"#94A3B8", marginTop:4 }}>{fmtShort(selectedDate)} · {staffLabel}</div>
            </div>
          )}

          {selectedDate && slotsError && !loadingSlots && (
            <div style={{ ...S.card, background:"#FFF1F2", borderColor:"#FCA5A5", display:"flex", gap:10, alignItems:"center" }}>
              <Ic.Alert s={16}/><span style={{ fontSize:12, color:"#991B1B" }}>{slotsError}</span>
            </div>
          )}

          {selectedDate && !loadingSlots && !slotsError && slots.length === 0 && (
            <div style={{ ...S.card, textAlign:"center", padding:"38px 16px", background:"#FAFAFA" }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#0F172A", marginBottom:6 }}>{fmtDate(selectedDate)}</div>
              <div style={{ fontSize:13, color:"#64748B", fontWeight:600 }}>No time slots available</div>
              <div style={{ fontSize:11, color:"#94A3B8", marginTop:4 }}>Please choose another date.</div>
            </div>
          )}

          {selectedDate && !loadingSlots && !slotsError && slots.length > 0 && (
            <div style={{ ...S.card, padding:14 }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#0F172A", marginBottom:2 }}>{fmtDate(selectedDate)}</div>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:12 }}>
                <Ic.Globe s={11}/>
                <span style={{ fontSize:11, color:"#94A3B8" }}>{tz.abbr} · {dur} min slots</span>
              </div>

              {[["Morning",morning],["Afternoon",afternoon]].map(([period,group]) => group.length > 0 && (
                <div key={period} style={{ marginBottom:14 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#94A3B8", letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:6 }}>
                    {period} · {group.filter(s=>s.available).length} available
                  </div>
                  <div className="slot-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:5 }}>
                    {group.map(slot => {
                      const sel = selectedSlot?.key === slot.key;
                      const isPast = slot.pastSlot;
                      return (
                        <button key={slot.key} disabled={!slot.available} onClick={() => slot.available && onSlotSelect(slot)} style={{
                          padding:"8px 4px", borderRadius:7, fontSize:12, fontWeight:600,
                          border: sel ? "none" : "1.5px solid #E2E8F0",
                          background: sel ? "#004e42" : slot.available ? "#fff" : isPast ? "#F8F8F8" : "#F5FAFA",
                          color: sel ? "#fff" : slot.available ? "#0F172A" : isPast ? "#D1D5DB" : "#CBD5E1",
                          cursor: slot.available ? "pointer" : "default",
                          textDecoration: isPast ? "line-through" : !slot.available ? "line-through" : "none",
                          opacity: isPast ? 0.5 : 1,
                          position:"relative", transition:"all .1s",
                        }}>
                          {slot.label}
                          {slot.available && slot.availableStaffIds.length > 1 && (
                            <span style={{ position:"absolute", top:2, right:3, fontSize:8, fontWeight:700, color:"#22C55E", lineHeight:1 }}>{slot.availableStaffIds.length}</span>
                          )}
                          {slot.available && slot.availableStaffIds.length <= 1 && (
                            <span style={{ position:"absolute", top:3, right:4, width:6, height:6, borderRadius:"50%", background:"#22C55E" }}/>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* ── Available staff for selected slot ── */}
              {selectedSlot?.available && (() => {
                const slotStaff = (allStaff || staff || []).filter(s => selectedSlot.availableStaffIds.includes(s.id));
                if (!slotStaff.length) return null;
                return (
                  <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #F1F5F9" }}>
                    <div style={{ fontSize:10, fontWeight:700, color:"#94A3B8", letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:8 }}>
                      Available for {selectedSlot.label} ({slotStaff.length})
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      {slotStaff.map(person => (
                        <div key={person.id} style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <StaffAvatar person={person} photos={staffPhotos || {}} size={28} />
                          <div>
                            <div style={{ fontSize:12, fontWeight:600, color:"#0F172A" }}>{person.displayName}</div>
                            <div style={{ fontSize:10, color:"#94A3B8" }}>{(CONFIG.staffMeta[person.emailAddress?.toLowerCase()] || {}).title || person.role}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div style={{ display:"flex", alignItems:"center", gap:12, marginTop:8, fontSize:11, color:"#94A3B8" }}>
                <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                  <span style={{ display:"inline-block", width:6, height:6, borderRadius:"50%", background:"#22C55E" }}/>
                  Available
                </div>
              </div>
              {(parseDuration(service?.preBuffer) > 0 || parseDuration(service?.postBuffer) > 0) && (
                <div style={{ fontSize:10, color:"#94A3B8", marginTop:6 }}>
                  Includes {parseDuration(service?.preBuffer) > 0 ? `${parseDuration(service.preBuffer)} min pre-buffer` : ""}{parseDuration(service?.preBuffer) > 0 && parseDuration(service?.postBuffer) > 0 ? " + " : ""}{parseDuration(service?.postBuffer) > 0 ? `${parseDuration(service.postBuffer)} min post-buffer` : ""}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// STEP 4 — Contact Details (dynamic fields from service config)
// ═════════════════════════════════════════════════════════════════════════════
function DetailsStep({ form, onChange, errors, service, customQuestions, autofilled = {}, disabled = false }) {
  // Resolve which custom questions apply to this service. Questions that ask
  // for a built-in field (e.g. "Company Name") are hidden and answered from it.
  const svcQuestions = (service?.customQuestions || []).filter(sq => !autofilled[sq.questionId]).map(sq => {
    const def = customQuestions.find(q => q.id === sq.questionId);
    return def ? { ...def, isRequired: sq.isRequired } : null;
  }).filter(Boolean);

  // Keep only characters valid in a phone number: digits, spaces, +, -, (, ), .
  const sanitizePhone = (v) => (v || "").replace(/[^0-9+()\-.\s]/g, "");

  const field = (name, label, type="text", ph="", req=true) => (
    <div style={{ marginBottom:14 }}>
      <label style={S.label}>{label}{req ? <span style={{color:"#EF4444"}}> *</span> : <span style={{color:"#94A3B8",fontWeight:400,textTransform:"none"}}> (optional)</span>}</label>
      <input
        type={type}
        value={form[name]||""}
        onChange={e => onChange(name, type === "tel" ? sanitizePhone(e.target.value) : e.target.value)}
        onKeyDown={type === "tel" ? (e) => {
          if (e.ctrlKey || e.metaKey || e.altKey) return;
          if (e.key.length === 1 && !/[0-9+()\-.\s]/.test(e.key)) e.preventDefault();
        } : undefined}
        onPaste={type === "tel" ? (e) => {
          const text = (e.clipboardData || window.clipboardData).getData("text");
          e.preventDefault();
          onChange(name, sanitizePhone((form[name] || "") + text));
        } : undefined}
        inputMode={type === "tel" ? "tel" : undefined}
        placeholder={ph}
        style={{ ...S.input, borderColor:errors[name]?"#FCA5A5":"#E2E8F0" }}
        onFocus={e=>e.target.style.borderColor="#004e42"} onBlur={e=>e.target.style.borderColor=errors[name]?"#FCA5A5":"#E2E8F0"}/>
      {errors[name] && <span style={{ fontSize:11, color:"#EF4444", marginTop:3, display:"block" }}>{errors[name]}</span>}
    </div>
  );

  // <fieldset disabled> natively locks every input/textarea/radio inside while
  // the booking is in flight, so nothing can change mid-submit.
  return (
    <fieldset disabled={disabled} aria-busy={disabled || undefined} style={{ border:"none", margin:0, padding:0, minWidth:0, opacity: disabled ? 0.7 : 1, transition:"opacity .15s" }}>
      <h2 style={S.h2}>Your details</h2>
      <p style={S.sub}>Fill in the required information for your <strong style={{color:"#0F172A"}}>{service?.displayName}</strong> session.</p>

      {/* ── Core customer fields ── */}
      <div className="details-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
        <div>{field("fullName","Full name","text","Alex Johnson",true)}</div>
        <div>{field("email","Email address","email","alex@company.com",true)}</div>
        <div>{field("company","Company","text","Contoso Ltd",true)}</div>
        <div>{field("phone","Phone number","tel","+1 (555) 123-4567",true)}</div>
      </div>

      {/* Customer notes — optional */}
      <div style={{ marginBottom:14 }}>
        <label style={S.label}>Notes <span style={{color:"#94A3B8",fontWeight:400,textTransform:"none"}}>(optional)</span></label>
        <textarea value={form.notes||""} onChange={e=>onChange("notes",e.target.value)} rows={3}
          placeholder="Anything you'd like us to know before the session…"
          style={{ ...S.input, resize:"vertical", lineHeight:1.6 }}
          onFocus={e=>e.target.style.borderColor="#004e42"} onBlur={e=>e.target.style.borderColor="#E2E8F0"}/>
      </div>

      {/* ── Custom questions from Graph ── */}
      {svcQuestions.length > 0 && (
        <div style={{ marginTop:8, paddingTop:14, borderTop:"1px solid #E2E8F0" }}>
          <div style={{ fontSize:10, fontWeight:700, color:"#94A3B8", letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:10 }}>
            Additional questions
          </div>
          {svcQuestions.map(q => {
            const key = `cq_${q.id}`;
            const isRadio = q.answerInputType === "radioButton" && q.answerOptions?.length;

            if (isRadio) {
              return (
                <div key={key} style={{ marginBottom:14 }}>
                  <label style={S.label}>{q.displayName}{q.isRequired ? <span style={{color:"#EF4444"}}> *</span> : <span style={{color:"#94A3B8",fontWeight:400,textTransform:"none"}}> (optional)</span>}</label>
                  <div style={{ display:"flex", flexDirection:"column", gap:6, marginTop:4 }}>
                    {q.answerOptions.map(opt => {
                      const selected = form[key] === opt;
                      return (
                        <label key={opt} onClick={() => onChange(key, opt)} style={{
                          display:"flex", alignItems:"center", gap:10, padding:"8px 12px",
                          border:`1.5px solid ${selected ? "#004e42" : errors[key] ? "#FCA5A5" : "#E2E8F0"}`,
                          borderRadius:8, cursor:"pointer", transition:"all .1s",
                          background: selected ? "#F0FAF8" : "#fff",
                        }}>
                          <div style={{
                            width:16, height:16, borderRadius:"50%",
                            border: `2px solid ${selected ? "#004e42" : "#CBD5E1"}`,
                            display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
                          }}>
                            {selected && <div style={{ width:8, height:8, borderRadius:"50%", background:"#004e42" }}/>}
                          </div>
                          <span style={{ fontSize:13, color:"#0F172A" }}>{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                  {errors[key] && <span style={{ fontSize:11, color:"#EF4444", marginTop:3, display:"block" }}>{errors[key]}</span>}
                </div>
              );
            }

            // Text input fallback
            return (
              <div key={key}>
                {field(key, q.displayName, "text", "", q.isRequired)}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop:14, padding:"10px 14px", background:"#FAFAFA", border:"1px solid #E2E8F0", borderRadius:8 }}>
        <p style={{ fontSize:12, color:"#64748B", margin:0, lineHeight:1.6 }}>
          By submitting you consent to Mekorma storing your details and creating a Lead in Dynamics 365. <a href="#" style={{color:"#004e42"}}>Privacy policy ↗</a>
        </p>
      </div>
    </fieldset>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ATTRIBUTION CARD  —  shown on confirmation; mirrors what is written to Lead
// ═════════════════════════════════════════════════════════════════════════════
function AttributionCard({ attr, correlationId, appointmentId }) {
  const [expanded, setExpanded] = useState(false);
  if (!attr) return null;

  // Click IDs that are present
  const clickIds = [
    ["gclid",     attr.gclid,     "Google Ads"],
    ["gbraid",    attr.gbraid,    "Google Ads (app)"],
    ["wbraid",    attr.wbraid,    "Google Ads (web)"],
    ["fbclid",    attr.fbclid,    "Meta Ads"],
    ["msclkid",   attr.msclkid,   "Microsoft Advertising"],
    ["li_fat_id", attr.li_fat_id, "LinkedIn Ads"],
    ["ttclid",    attr.ttclid,    "TikTok Ads"],
    ["twclid",    attr.twclid,    "Twitter / X Ads"],
    ["dclid",     attr.dclid,     "DV360"],
  ].filter(([, v]) => v);

  const hasClickIds = clickIds.length > 0;

  // Channel pill colour
  const channelColor = {
    "Paid Search":     ["#004e42","#E8F2EF"],
    "Display":         ["#006b5c","#F5F3FF"],
    "Paid Social":     ["#0078D4","#EFF6FF"],
    "Organic Social":  ["#0891B2","#ECFEFF"],
    "Email":           ["#5a9e3b","#FFFBEB"],
    "Referral":        ["#059669","#ECFDF5"],
    "Affiliate":       ["#7C3AED","#EDE9FE"],
    "Direct":          ["#64748B","#F1F5F9"],
  }[attr.channel] || ["#94A3B8","#F5FAFA"];

  const Row = ({ label, value, mono=true }) => value ? (
    <div style={{ display:"contents" }}>
      <span style={{ fontSize:11, fontWeight:600, color:"#94A3B8", whiteSpace:"nowrap", paddingRight:8 }}>{label}</span>
      <span style={{ fontSize:11, color:"#0F172A", fontFamily:mono?"monospace":"inherit", wordBreak:"break-all" }}>{value}</span>
    </div>
  ) : null;

  return (
    <div style={{ ...S.card, textAlign:"left", background:"#F4FAF8", borderColor:"#B3CECA", marginBottom:12 }}>
      {/* Header row */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
        <div style={{ fontSize:11, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.05em" }}>
          Attribution captured
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ ...S.tag(channelColor[0], channelColor[1]), fontSize:11 }}>{attr.channel}</span>
          {hasClickIds && (
            <span style={{ ...S.tag("#0078D4","#EFF6FF"), fontSize:10 }}>
              {clickIds[0][2]} ID
            </span>
          )}
        </div>
      </div>

      {/* UTM grid — always visible */}
      <div style={{ display:"grid", gridTemplateColumns:"auto 1fr", gap:"5px 0", marginBottom:10 }}>
        <Row label="utm_source"   value={attr.utm_source}/>
        <Row label="utm_medium"   value={attr.utm_medium}/>
        <Row label="utm_campaign" value={attr.utm_campaign || "—"} mono={false}/>
        <Row label="utm_term"     value={attr.utm_term || "—"} mono={false}/>
        <Row label="utm_content"  value={attr.utm_content || "—"} mono={false}/>
        {attr.utm_id && <Row label="utm_id" value={attr.utm_id}/>}
      </div>

      {/* Click IDs — shown if present */}
      {hasClickIds && (
        <div style={{ borderTop:"1px solid #DBEAFE", paddingTop:8, marginBottom:10 }}>
          <div style={{ fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:6 }}>Click IDs</div>
          <div style={{ display:"grid", gridTemplateColumns:"auto 1fr", gap:"5px 0" }}>
            {clickIds.map(([key, val]) => (
              <Row key={key} label={key} value={val.length > 28 ? val.slice(0,28)+"…" : val}/>
            ))}
          </div>
        </div>
      )}

      {/* Expand / collapse for context fields */}
      <button onClick={() => setExpanded(e => !e)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, color:"#004e42", fontWeight:600, padding:0, display:"flex", alignItems:"center", gap:4 }}>
        {expanded ? "▲ Hide context" : "▼ Show context & Dynamics field mapping"}
      </button>

      {expanded && (
        <div style={{ marginTop:10, borderTop:"1px solid #DBEAFE", paddingTop:10 }}>
          {/* Context fields */}
          <div style={{ fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:6 }}>Context</div>
          <div style={{ display:"grid", gridTemplateColumns:"auto 1fr", gap:"5px 0", marginBottom:12 }}>
            <Row label="correlationId" value={correlationId}/>
            {appointmentId && <Row label="appointmentId" value={appointmentId}/>}
            <Row label="referrer"    value={attr.referrer || "none"} mono={false}/>
            <Row label="capturedAt"  value={attr.capturedAt}/>
            {attr.rawParams && <Row label="rawParams" value={attr.rawParams.length > 60 ? attr.rawParams.slice(0,60)+"…" : attr.rawParams}/>}
          </div>

          {/* Dynamics 365 Lead field mapping */}
          <div style={{ fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:6 }}>Dynamics 365 Lead fields</div>
          <div style={{ display:"grid", gridTemplateColumns:"auto 1fr", gap:"5px 0" }}>
            {[
              ["msdyn_correlationid",  correlationId],
              ["msdyn_utmsource",      attr.utm_source],
              ["msdyn_utmmedium",      attr.utm_medium],
              ["msdyn_utmcampaign",    attr.utm_campaign],
              ["msdyn_utmterm",        attr.utm_term],
              ["msdyn_utmcontent",     attr.utm_content],
              ["msdyn_utmid",          attr.utm_id],
              ["msdyn_channel",        attr.channel],
              ["msdyn_clickidsource",  attr.clickIdSource],
              ["msdyn_gclid",          attr.gclid],
              ["msdyn_msclkid",        attr.msclkid],
              ["msdyn_fbclid",         attr.fbclid],
              ["msdyn_landingurl",     attr.landingUrl?.slice(0,50)+"…"],
              ["msdyn_graphappointmentid", appointmentId],
            ].filter(([,v]) => v).map(([k,v]) => <Row key={k} label={k} value={v}/>)}
          </div>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// STEP 5 — Confirmation
// ═════════════════════════════════════════════════════════════════════════════
function ConfirmationStep({ booking, session, onReschedule, onCancel, onNewBooking, staff, staffPhotos }) {
  const svc = booking.service;
  const dur = parseDuration(svc?.defaultDuration);
  const meta = CONFIG.serviceMeta[svc?.displayName] || {};
  // Who they're meeting: the chosen person, else whoever the server assigned
  // for "Anyone available" (booking-create returns assignedStaff).
  const assigned = booking.assignedStaff
    ? (staff || []).find(s => s.id === booking.assignedStaff.id) || booking.assignedStaff
    : null;
  const person = booking.staff && booking.staff !== "any" ? booking.staff : assigned;
  const staffName = person?.displayName || "Next available";
  const displayStaff = person?.displayName ? [person] : (staff || []);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  // Show "Booking cancelled" only after Bookings confirms it; on failure keep
  // the dialog open with the error so the visitor can retry.
  const confirmCancel = async () => {
    if (cancelling) return;
    setCancelling(true); setCancelError(null);
    try {
      await onCancel?.();
      setCancelled(true); setShowCancelConfirm(false);
    } catch (err) {
      setCancelError(err?.message || "We couldn't cancel this booking. Please try again.");
    } finally {
      setCancelling(false);
    }
  };
  const closeCancelDialog = () => { if (!cancelling) { setShowCancelConfirm(false); setCancelError(null); } };

  const actionBtn = (icon, label, onClick, variant="default") => {
    const styles = {
      default: { background:"#fff", border:"1.5px solid #E2E8F0", color:"#374151", hoverBg:"#F8FAFC" },
      danger:  { background:"#fff", border:"1.5px solid #FCA5A5", color:"#DC2626", hoverBg:"#FEF2F2" },
      primary: { background:"#004e42", border:"1.5px solid #004e42", color:"#fff", hoverBg:"#003d34" },
    };
    const s = styles[variant];
    return (
      <button onClick={onClick} style={{
        display:"flex", alignItems:"center", justifyContent:"center", gap:8,
        padding:"10px 18px", borderRadius:10, fontWeight:600, fontSize:13,
        cursor:"pointer", background:s.background, border:s.border, color:s.color,
        transition:"all .15s", flex:1, minWidth:0,
      }}>
        {icon}{label}
      </button>
    );
  };

  return (
    <div style={{ textAlign:"center" }}>
      {cancelled ? (
        <>
          <div style={{ width:68, height:68, borderRadius:"50%", background:"#DC2626", margin:"0 auto 16px", display:"flex", alignItems:"center", justifyContent:"center", animation:"popIn .5s cubic-bezier(.175,.885,.32,1.275)" }}>
            <Ic.XCircle s={30} c="#fff"/>
          </div>
          <h2 style={{ ...S.h2, textAlign:"center" }}>Booking cancelled</h2>
          <p style={{ ...S.sub, textAlign:"center" }}>A cancellation notice has been sent to <strong style={{color:"#0F172A"}}>{booking.form.email}</strong>.</p>
          <div style={{ marginTop:24 }}>
            {actionBtn(<Ic.Plus s={15} c="#fff"/>, "Book a new session", onNewBooking, "primary")}
          </div>
        </>
      ) : (
        <>
          {/* Header — centered */}
          <div style={{ textAlign:"center", marginBottom:16 }}>
            <div style={{ width:48, height:48, borderRadius:"50%", background:"#004e42", display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:10, animation:"popIn .5s cubic-bezier(.175,.885,.32,1.275)" }}>
              <Ic.Check s={22} c="#fff"/>
            </div>
            <h2 style={{ fontSize:20, fontWeight:700, color:"#0F172A", margin:"0 0 4px" }}>{booking.rescheduled ? "You're rescheduled!" : "You're booked!"}</h2>
            <p style={{ fontSize:13, color:"#64748B", margin:0 }}>
              {booking.rescheduled && !booking.previousStillActive ? "Your previous booking was cancelled. " : ""}
              Invite sent to <strong style={{color:"#0F172A"}}>{booking.form.email}</strong>
            </p>
          </div>

          {booking.previousStillActive && (
            <div role="alert" style={{ background:"#FFFBEB", border:"1px solid #FCD34D", borderRadius:10, padding:"10px 12px", marginBottom:14, fontSize:12, color:"#92400E", textAlign:"left", lineHeight:1.5 }}>
              Your new time is booked, but we couldn't cancel your previous booking — it's still active. Please cancel it from your earlier confirmation email, or email <a href="mailto:bookings@mekorma.com" style={{ color:"#92400E" }}>bookings@mekorma.com</a>.
            </div>
          )}

          {/* Compact summary card */}
          <div style={{ background:"#F8FAFC", border:"1px solid #E2E8F0", borderRadius:12, padding:"14px 16px", marginBottom:14, textAlign:"left" }}>
            {/* Service row */}
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12, paddingBottom:12, borderBottom:"1px solid #E2E8F0" }}>
              <div style={{ width:4, alignSelf:"stretch", borderRadius:3, background:meta.accent||"#004e42", flexShrink:0 }}/>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:"#0F172A", lineHeight:1.3 }}>{svc?.displayName}</div>
                <div style={{ display:"flex", alignItems:"center", gap:5, marginTop:3 }}><Ic.Clock s={11}/><span style={{ fontSize:12, color:"#64748B" }}>{dur} min · Online</span></div>
              </div>
            </div>

            {/* Details grid — 3 columns */}
            <div className="confirm-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"0 16px", alignItems:"start" }}>
              <div>
                <div style={{ fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:3 }}>Date</div>
                <div style={{ fontSize:13, color:"#0F172A", fontWeight:600 }}>{fmtDate(booking.date)}</div>
              </div>
              <div>
                <div style={{ fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:3 }}>Time</div>
                <div style={{ fontSize:13, color:"#0F172A", fontWeight:600 }}>{booking.timeSlot?.label} {session.tz.abbr}</div>
              </div>
              <div>
                <div style={{ fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:3 }}>With</div>
                <div style={{ display:"flex", alignItems:"center" }}>
                  {displayStaff.slice(0, 3).map((p, i) => (
                    <div key={p.id} style={{ marginLeft: i === 0 ? 0 : -8, zIndex: displayStaff.length - i, position:"relative" }} title={p.displayName}>
                      <StaffAvatar person={p} photos={staffPhotos || {}} size={26} border="#F8FAFC" />
                    </div>
                  ))}
                  {displayStaff.length > 3 && (
                    <div style={{ marginLeft:-6, width:26, height:26, borderRadius:"50%", background:"#E2E8F0", border:"2px solid #F8FAFC", display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, color:"#64748B", zIndex:0 }}>
                      +{displayStaff.length - 3}
                    </div>
                  )}
                  <span style={{ fontSize:12, color:"#0F172A", fontWeight:600, marginLeft:8 }}>{staffName}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display:"flex", gap:8, marginBottom:12 }}>
            {actionBtn(<Ic.Calendar s={14}/>, "Reschedule", onReschedule)}
            {actionBtn(<Ic.XCircle s={14}/>, "Cancel", () => setShowCancelConfirm(true))}
          </div>

          {/* Cancel confirmation modal */}
          {showCancelConfirm && !cancelled && (
            <div
              style={{ position:"fixed", inset:0, zIndex:100, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(15,23,42,.4)", backdropFilter:"blur(4px)" }}
              onClick={closeCancelDialog}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ background:"#fff", borderRadius:16, padding:"32px 36px 28px", maxWidth:400, width:"calc(100% - 32px)", boxShadow:"0 20px 60px rgba(0,0,0,.15)", animation:"fadeUp .2s ease both" }}
              >
                <div style={{ width:48, height:48, borderRadius:"50%", background:"#FEF2F2", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
                  <Ic.XCircle s={22} c="#DC2626"/>
                </div>
                <h3 style={{ fontSize:18, fontWeight:700, color:"#0F172A", textAlign:"center", margin:"0 0 8px" }}>
                  Cancel booking?
                </h3>
                <p style={{ fontSize:14, color:"#64748B", textAlign:"center", margin:"0 0 8px", lineHeight:1.6 }}>
                  This will cancel your booking with <strong style={{ color:"#0F172A" }}>{staffName}</strong>.
                </p>
                <p style={{ fontSize:12, color:"#94A3B8", textAlign:"center", margin:"0 0 24px" }}>
                  This action cannot be undone.
                </p>
                {cancelError && (
                  <div role="alert" style={{ background:"#FFF1F2", border:"1px solid #FCA5A5", borderRadius:10, padding:"10px 12px", margin:"-8px 0 16px", fontSize:12, color:"#991B1B", textAlign:"left", lineHeight:1.5 }}>
                    {cancelError} Your booking is still active — try again, or email <a href="mailto:bookings@mekorma.com" style={{ color:"#991B1B" }}>bookings@mekorma.com</a>.
                  </div>
                )}
                <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
                  <button
                    onClick={closeCancelDialog}
                    disabled={cancelling}
                    style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", gap:8, padding:"10px 24px", borderRadius:10, fontWeight:600, fontSize:14, border:"1.5px solid #E2E8F0", cursor: cancelling ? "default" : "pointer", background:"#fff", color:"#374151", flex:1, transition:"all .15s", opacity: cancelling ? 0.6 : 1 }}
                  >
                    Never mind
                  </button>
                  <button
                    onClick={confirmCancel}
                    disabled={cancelling}
                    style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", gap:8, padding:"10px 24px", borderRadius:10, fontWeight:600, fontSize:14, border:"none", cursor: cancelling ? "default" : "pointer", background:"#EF4444", color:"#fff", flex:1, transition:"all .15s", opacity: cancelling ? 0.8 : 1 }}
                  >
                    {cancelling
                      ? <><Ic.Loader s={14}/> Cancelling…</>
                      : <><Ic.XCircle s={14} c="#fff"/> {cancelError ? "Try again" : "Confirm"}</>}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Attribution card hidden from end users */}

          <p style={{ fontSize:12, color:"#94A3B8", marginTop:16, lineHeight:1.6 }}>
            Questions? <a href="mailto:bookings@mekorma.com" style={{color:"#004e42"}}>bookings@mekorma.com</a>
          </p>
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// WIZARD STEP DEFINITIONS
// ═════════════════════════════════════════════════════════════════════════════
// Fixed total set of wizard steps. `index` is the internal step number used
// by setStep(). `isSkipped(appSettings)` returns true when that step should
// be hidden entirely from the progress bar and from back/advance navigation.
const WIZARD_STEPS = [
  {
    index: 0,
    label: "Type",
    isSkipped: (s) => {
      const mode = s?.steps?.relationshipType?.mode;
      return mode === "skipUrl" || mode === "skipService" || mode === "skip"; // legacy
    },
  },
  { index: 1, label: "Session", isSkipped: () => false },
  { index: 2, label: "Staff",   isSkipped: () => false },
  { index: 3, label: "Time",    isSkipped: () => false },
  { index: 4, label: "Details", isSkipped: () => false },
];

function getActiveSteps(appSettings) {
  return WIZARD_STEPS.filter(s => !s.isSkipped(appSettings));
}

// ═════════════════════════════════════════════════════════════════════════════
// PROGRESS BAR
// ═════════════════════════════════════════════════════════════════════════════
function Progress({ step, activeSteps }) {
  const currentIdx = activeSteps.findIndex(s => s.index === step);
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", marginBottom:26 }}>
      {activeSteps.map((s, i) => {
        const done = currentIdx >= 0 && i < currentIdx;
        const active = i === currentIdx;
        return (
          <div key={s.index} style={{ display:"flex", alignItems:"center" }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
              <div style={{ width:28, height:28, borderRadius:"50%", background:done?"#88c96c":active?"#E8F2EF":"#F1F5F9", border:active?"2px solid #004e42":done?"none":"2px solid #E2E8F0", display:"flex", alignItems:"center", justifyContent:"center", transition:"all .2s" }}>
                {done ? <Ic.Check s={13} c="#fff"/> : <span style={{ fontSize:12, fontWeight:700, color:active?"#004e42":"#94A3B8" }}>{i+1}</span>}
              </div>
              <span className="progress-label" style={{ fontSize:10, fontWeight:600, color:active?"#004e42":done?"#64748B":"#94A3B8", whiteSpace:"nowrap" }}>{s.label}</span>
            </div>
            {i < activeSteps.length-1 && <div className="progress-connector" style={{ width:30, height:2, background:done?"#88c96c":"#E2E8F0", margin:"0 4px", marginBottom:16, transition:"background .3s" }}/>}
          </div>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SIDEBAR
// ═════════════════════════════════════════════════════════════════════════════
function Sidebar({ booking, tz, correlationId }) {
  const svc = booking.service;
  if (!svc && !booking.relationshipType) return null;
  const dur = svc ? parseDuration(svc.defaultDuration) : null;
  const staffName = booking.staff && booking.staff !== "any" ? booking.staff.displayName : booking.staff === "any" ? "Any available" : null;
  const rows = [["Type",booking.relationshipType?.label],["Session",svc?.displayName],["Duration",dur?`${dur} min`:null],["With",staffName],["Date",booking.date?fmtShort(booking.date):null],["Time",booking.timeSlot?`${booking.timeSlot.label} ${tz.abbr}`:null]].filter(([,v])=>v);
  return (
    <div style={{ ...S.card, padding:"14px 16px", background:"#F5FAFA", borderColor:"#C5DDD9" }}>
      <div style={{ fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:10 }}>Your selection</div>
      {rows.map(([k,v]) => (
        <div key={k} style={{ marginBottom:8 }}>
          <div style={{ fontSize:9, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.04em" }}>{k}</div>
          <div style={{ fontSize:12, fontWeight:600, color:"#0F172A", marginTop:1 }}>{v}</div>
        </div>
      ))}
      <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid #E2E8F0" }}>
        <div style={{ fontSize:9, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:4 }}>Session ID</div>
        <div style={{ fontSize:10, color:"#94A3B8", fontFamily:"monospace", wordBreak:"break-all" }}>{correlationId?.slice(0,18)}…</div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ROOT APP
// ═════════════════════════════════════════════════════════════════════════════
export default function BookingApp() {
  const { linkId } = useParams();
  const isMobile = useIsMobile();
  const [step,     setStep]     = useState(0);
  const stepRef = useRef(step); // current step for async callbacks
  stepRef.current = step;
  const [dir,      setDir]      = useState(1);
  const [animKey,  setAnimKey]  = useState(0);

  // ?reschedule=<manage token> (from the Manage page). The token can cancel the
  // booking, so it must never be stored on the Dataverse Lead: attribution
  // capture strips it, and it's removed from the address bar after mount.
  const [rescheduleToken] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("reschedule") || null; } catch { return null; }
  });
  useEffect(() => {
    if (!rescheduleToken) return;
    try { window.history.replaceState(window.history.state, "", withoutParam(window.location.href, "reschedule")); } catch { /* cosmetic */ }
  }, [rescheduleToken]);

  const [session, setSession] = useState(() => ({
    correlationId: uuid(),
    attr: captureAttribution(linkId),   // full attribution object — UTMs + click IDs + context
    tz: getTZ(),
    startedAt: new Date().toISOString(),
  }));

  // ── Link-based service filtering ───────────────────────────────────────────
  const [linkedServiceIds, setLinkedServiceIds] = useState(null); // null = loading, [] = resolved
  // Map of serviceId → mek_bookingbusinessid (empty string when the mapping
  // has no per-row business). Used to tag fetched Graph services with the
  // right business so staff/availability/booking calls target the correct
  // Bookings business when a link mixes shared + personal businesses.
  const [linkedServiceBizMap, setLinkedServiceBizMap] = useState({});
  const [linkError, setLinkError] = useState(null);
  const [linkDisabled, setLinkDisabled] = useState(false);
  const [linkedCampaignId, setLinkedCampaignId] = useState(null);

  // ── App-wide settings (from /api/settings via booking-link-resolve) ───────
  // Drives: skip Relationship step via URL param, UTM hide filtering,
  // custom URL parameter capture, and feature toggles.
  const [appSettings, setAppSettings] = useState(null);

  // ── Relationship types (Lead.mek_relationshiptype OptionSet) ──────────────
  const [relationshipTypes, setRelationshipTypes] = useState([]);
  const [loadingRelTypes, setLoadingRelTypes] = useState(true);
  const [relTypesError, setRelTypesError] = useState(null);

  // ── Graph data ─────────────────────────────────────────────────────────────
  const [services,      setServices]      = useState([]);
  const [loadingSvc,    setLoadingSvc]    = useState(true);
  const [svcError,      setSvcError]      = useState(null);

  const [allStaff,      setAllStaff]      = useState([]);   // full roster (fetched once)
  const [allStaffPhotos,setAllStaffPhotos]= useState({});   // email → data URI (fetched once)
  const [customQuestions,setCustomQuestions]= useState([]);  // business-level custom questions
  const [staff,         setStaff]         = useState([]);   // staff for selected service
  const [loadingStaff,  setLoadingStaff]  = useState(false);
  const [staffError,    setStaffError]    = useState(null);
  const [staffPhotos,   setStaffPhotos]   = useState({});   // email → data URI (service-scoped)

  const [staffAvail,    setStaffAvail]    = useState(null);  // raw /api/staff-availability response for booking.date
  const [loadingSlots,  setLoadingSlots]  = useState(false);
  const [slotsError,    setSlotsError]    = useState(null);
  const [refreshKey,    setRefreshKey]    = useState(0);     // bump to re-fetch availability
  const [slotNotice,    setSlotNotice]    = useState(null);  // shown on the Time step when a picked time is no longer free
  const [staffNotice,   setStaffNotice]   = useState(null);  // shown on the Staff step when the server refused the staff choice
  // The server's staff-choice rule, when it refused this page's (stale tab /
  // settings changed): { serviceId, patch } applied over staffChoiceFor().
  const [choiceOverride, setChoiceOverride] = useState(null);
  const [rescheduleNotice, setRescheduleNotice] = useState(null); // banner while rescheduling from the Manage page
  const [liveTick,      setLiveTick]      = useState(0);     // bumps trigger a silent availability re-check
  const availSeq    = useRef(0);   // latest staff-availability request; older responses are dropped
  const staffAvailDay = useRef(null); // ymd that staffAvail describes (it lags a render behind a date click)
  const lastAvailAt = useRef(0);   // when staff availability was last requested

  // ── Booking state ──────────────────────────────────────────────────────────
  const [booking, setBooking] = useState({
    relationshipType: null,
    service: null,
    staff: undefined,     // undefined = not chosen; "any" = any available; object = specific
    date: null,
    timeSlot: null,
    appointmentId: null,
    cancelToken: null,       // capability for Cancel / Reschedule (issued by booking-create)
    assignedStaff: null,     // { id, displayName } the server assigned for "Anyone available"
    businessId: null,        // Bookings business the booking was made in (from booking-create)
    rescheduleFrom: null,    // { appointmentId, businessId, cancelToken } — booking being replaced
    rescheduled: false,      // confirmation: this booking replaced an earlier one
    previousStillActive: false, // confirmation: the earlier booking couldn't be cancelled
    form: { fullName:"", email:"", company:"", phone:"", notes:"" },
  });

  const [submitting,  setSubmitting]  = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [formErrors,  setFormErrors]  = useState({});

  // ── 0. Resolve booking link to get allowed service IDs ───────────────────────
  useEffect(() => {
    if (!linkId) return;
    fetch(`/api/booking-links/resolve/${encodeURIComponent(linkId)}`)
      .then(async r => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          if (data.disabled) {
            setLinkDisabled(true);
            throw new Error(data.error || "This booking link has been disabled");
          }
          throw new Error(data.error || "Link not found");
        }
        return r.json();
      })
      .then(data => {
        setLinkedServiceIds(data.serviceIds || []);
        // Stash businessId per service so the services fetch below can fan
        // out per-business and tag each Graph service with the right one.
        const bizMap = {};
        for (const s of (data.services || [])) {
          if (s?.id) bizMap[s.id] = (s.businessId || "").trim();
        }
        setLinkedServiceBizMap(bizMap);
        if (data.link?.campaignId) setLinkedCampaignId(data.link.campaignId);
        if (data.appSettings) setAppSettings(data.appSettings);
        console.log(`[BookingPlatform] Link resolved: ${data.link?.displayName}, ${data.serviceIds?.length} service(s), campaign=${data.link?.campaignId || "none"}`);
      })
      .catch(err => {
        console.error("[BookingPlatform] Link resolution failed:", err);
        setLinkError(err.message);
        setLinkedServiceIds([]);
      });
  }, [linkId]);

  // ── Auto-skip Relationship step when settings say so ──────────────────────
  // mode === "skipUrl"     : read a URL param, map via urlParamMap, advance past step 0.
  // mode === "skipService" : always advance past step 0; the relationship is
  //                          resolved on submit from a service custom-question answer.
  // mode === "show" (default): no auto-skip.
  // "skip" is accepted as a legacy alias for "skipUrl".
  const [skipChecked, setSkipChecked] = useState(false);
  useEffect(() => {
    if (skipChecked) return;
    // Wait until the link has resolved (gives us appSettings + linkedServiceIds).
    // If the server returned no settings (soft-fail), treat as "no skip" so we
    // don't block the loading screen waiting for state that will never arrive.
    if (linkedServiceIds === null) return;
    if (!appSettings) { setSkipChecked(true); return; }

    const rt = appSettings.steps?.relationshipType;
    const mode = rt?.mode;
    if (mode !== "skipUrl" && mode !== "skip" && mode !== "skipService") {
      setSkipChecked(true);
      return;
    }

    if (mode === "skipService") {
      console.log(`[BookingPlatform] Skipping Relationship step (skipService) — relationship will be resolved from service custom question on submit`);
      if (step === 0) {
        setDir(1); setAnimKey(k => k + 1); setStep(1);
      }
      setSkipChecked(true);
      return;
    }

    // skipUrl (and legacy "skip")
    if (relationshipTypes.length === 0) return; // wait for fetch

    const qp = new URLSearchParams(window.location.search);
    const urlValue = (qp.get(rt.urlParamName) || "").trim().toLowerCase();
    if (!urlValue) { setSkipChecked(true); return; }

    const mapping = (rt.urlParamMap || []).find(m => (m.urlValue || "").toLowerCase() === urlValue);
    if (!mapping) {
      console.warn(`[BookingPlatform] Relationship skip: URL value "${urlValue}" not in lookup map — showing step`);
      setSkipChecked(true);
      return;
    }

    const match = relationshipTypes.find(r => r.value === mapping.optionSetValue);
    if (!match) {
      console.warn(`[BookingPlatform] Relationship skip: optionSet value ${mapping.optionSetValue} not in fetched types`);
      setSkipChecked(true);
      return;
    }

    console.log(`[BookingPlatform] Skipping Relationship step — matched "${match.label}" from ?${rt.urlParamName}=${urlValue}`);
    setBooking(b => ({ ...b, relationshipType: match }));
    if (step === 0) {
      setDir(1); setAnimKey(k => k + 1); setStep(1);
    }
    setSkipChecked(true);
  }, [appSettings, linkedServiceIds, relationshipTypes, skipChecked, step]);

  // ── Extract configured custom URL parameters from the booking link ─────────
  const customParamValues = useMemo(() => {
    if (!appSettings?.customParameters?.length) return [];
    const qp = new URLSearchParams(window.location.search);
    return appSettings.customParameters
      .map(cp => {
        const value = qp.get(cp.urlParamName);
        if (value === null || value === "") return null;
        return { urlParamName: cp.urlParamName, value, mapTo: cp.mapTo };
      })
      .filter(Boolean);
  }, [appSettings]);

  // ── Fetch relationship type OptionSet from Lead entity ─────────────────────
  useEffect(() => {
    fetch("/api/lead-relationship-types")
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => {
        setRelationshipTypes(data.value || []);
        setLoadingRelTypes(false);
      })
      .catch(err => {
        console.error("[BookingPlatform] Relationship types fetch failed:", err);
        setRelTypesError(err.message || "Unknown error");
        setLoadingRelTypes(false);
      });
  }, []);

  // ── 1. Fetch services (per-business when linked), all staff, custom Qs ──────
  // Link flow: each mapping may target a different bookings business, so we
  //   fetch services once per unique business in the link and stitch the
  //   businessId onto each resulting Graph service. Mappings with no
  //   mek_bookingbusinessid fall back to the default via the empty-key bucket.
  // Link-less flow: fetch the default business only.
  useEffect(() => {
    // When a link is provided, wait for resolve so we know the biz map.
    if (linkId && linkedServiceIds === null) return;

    setLoadingSvc(true);

    (async () => {
      try {
        const bizBuckets = linkId
          ? [...new Set(
              (linkedServiceIds || []).map((id) => (linkedServiceBizMap[id] || "").trim())
            )]
          : [""]; // empty string = ask server for the default business

        const results = await Promise.all(
          bizBuckets.map((biz) =>
            graphGetServices(biz || undefined).then((svcs) => ({ biz, svcs }))
          )
        );

        const merged = [];
        for (const { biz, svcs } of results) {
          for (const svc of svcs) {
            merged.push({ ...svc, businessId: biz || "" });
          }
        }

        // When linked, keep only services actually in the link (a business may
        // expose more services than the mapping picks).
        const filtered = linkId && linkedServiceIds?.length
          ? merged.filter((s) => linkedServiceIds.includes(s.id))
          : merged;

        setServices(filtered);
        setLoadingSvc(false);
      } catch (e) {
        setSvcError(e.message || "Unknown error");
        setLoadingSvc(false);
      }
    })();
  }, [linkId, linkedServiceIds, linkedServiceBizMap]);

  // Fetch full staff roster + custom questions on mount (link-less default
  // business). Per-service staff refinement happens in the effect below when
  // a service is picked.
  useEffect(() => {
    graphGetStaffForService(undefined, [])
      .then(res => {
        const members = res.value || res;
        setAllStaff(members);
        console.log(`[BookingPlatform] All staff loaded: ${members.length} member(s)`);
        const emails = members.map(m => m.emailAddress).filter(Boolean);
        if (emails.length) {
          fetch(`/api/staff-photo?emails=${encodeURIComponent(emails.join(","))}`)
            .then(r => r.ok ? r.json() : null)
            .then(photos => { if (photos) setAllStaffPhotos(photos); })
            .catch(() => {});
        }
      })
      .catch(() => {});

    fetch("/api/custom-questions")
      .then(r => { if (!r.ok) console.warn(`[BookingPlatform] Custom questions fetch returned ${r.status}`); return r.ok ? r.json() : null; })
      .then(data => {
        const qs = data?.value ?? [];
        // Merge, don't replace: a service's own business's questions may have
        // loaded first (single-service links skip ahead quickly).
        setCustomQuestions(prev => mergeQuestionsById(prev, qs));
        console.log(`[BookingPlatform] Custom questions loaded: ${qs.length}`, qs.map(q => q.displayName));
      })
      .catch(err => console.warn("[BookingPlatform] Custom questions fetch failed:", err.message));
  }, []);

  // Custom question definitions are per Bookings business. A link can mix
  // businesses, so load the selected service's own business's questions too
  // (the fetch above only covers the default business) and merge by id —
  // otherwise that service's questions can't render.
  useEffect(() => {
    const biz = booking.service?.businessId;
    if (!biz) return;
    fetch(`/api/custom-questions?businessId=${encodeURIComponent(biz)}`)
      .then(r => { if (!r.ok) console.warn(`[BookingPlatform] Custom questions fetch for ${biz} returned ${r.status} — that service's questions won't show`); return r.ok ? r.json() : null; })
      .then(data => {
        const qs = data?.value ?? [];
        if (!qs.length) return;
        setCustomQuestions(prev => mergeQuestionsById(prev, qs));
        console.log(`[BookingPlatform] Custom questions loaded for ${biz}: ${qs.length}`);
      })
      .catch(err => console.warn(`[BookingPlatform] Custom questions fetch failed for ${biz}:`, err.message));
  }, [booking.service?.businessId]);

  // ── 2. Fetch staff when service changes ────────────────────────────────────
  useEffect(() => {
    const svc = booking.service;
    if (!svc) return;

    setStaff([]);
    setLoadingStaff(true);
    setStaffError(null);
    autoAdvancedStaff.current = false; // Reset so auto-advance can fire for new service
    // Reset downstream selections
    setBooking(b => ({ ...b, staff: undefined, date: null, timeSlot: null }));

    // A service with no staffMemberIds is bookable with anyone in its own
    // business: an empty id list returns that business's whole roster (the
    // same roster booking-create checks and assigns from).
    const staffIds = svc.staffMemberIds || [];
    if (!staffIds.length) console.log(`[BookingPlatform] Service "${svc.displayName}" has no staffMemberIds — using its business's whole roster`);

    graphGetStaffForService(svc.businessId || undefined, staffIds)
      .then(res => {
        const members = res.value || res;
        setStaff(members);
        // "Anyone available" only: auto-set the sentinel + default date so availability loads immediately
        if (staffChoiceFor(appSettings, svc).anyoneOnly) setBooking(b => ({ ...b, staff: "any", date: b.date || new Date() }));
        setLoadingStaff(false);

        // Fetch profile photos in background (non-blocking)
        const emails = members.map(m => m.emailAddress).filter(Boolean);
        if (emails.length) {
          console.log(`[BookingPlatform] Fetching photos for ${emails.length} staff member(s)`);
          fetch(`/api/staff-photo?emails=${encodeURIComponent(emails.join(","))}`)
            .then(r => r.ok ? r.json() : null)
            .then(photos => { if (photos) { console.log(`[BookingPlatform] Photos loaded:`, Object.keys(photos).filter(k => photos[k]).length, "found"); setStaffPhotos(photos); } })
            .catch(err => console.warn(`[BookingPlatform] Photo fetch failed (non-critical):`, err.message));
        }
      })
      .catch(e => { setStaffError(e.message||"Could not load staff"); setLoadingStaff(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.service?.id]);

  // ── 3. Fetch availability when date or staff selection changes ─────────────
  useEffect(() => {
    const { date, service, staff: selectedStaff } = booking;
    if (!date || !service) return;

    // Query specific staff, service staff, or all business staff (fallback)
    const staffIds = availabilityStaffIds(selectedStaff, staff, allStaff);

    if (!staffIds.length) {
      console.log(`[BookingPlatform] No staff IDs available to query availability`);
      setStaffAvail(null);
      setLoadingSlots(false);
      return;
    }
    console.log(`[BookingPlatform] Querying availability for ${staffIds.length} staff member(s)`);

    // Only the latest request may land — a slow response for a date the user
    // already left (or a background re-check) must never overwrite newer data.
    const seq = ++availSeq.current;
    lastAvailAt.current = Date.now();
    setStaffAvail(null);
    setLoadingSlots(true);
    setSlotsError(null);
    setBooking(b => ({ ...b, timeSlot: null }));

    graphGetStaffAvailability(service.businessId || undefined, staffIds, date, session.tz.zone, service.id)
      .then(res => { if (seq === availSeq.current) { staffAvailDay.current = ymdLocal(date); setStaffAvail(res); setLoadingSlots(false); } })
      .catch(e => { if (seq === availSeq.current) { setSlotsError(e.message||"Could not fetch availability"); setLoadingSlots(false); } });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.date?.toDateString(), (booking.staff && booking.staff !== "any" ? booking.staff.id : "any"), staff.length, refreshKey]);

  // ── 3a. Keep the Time step live ──────────────────────────────────────────────
  // Staff calendars change while the page is open (other visitors book, new
  // meetings land). Silently re-check the selected day when the visitor
  // arrives at / returns to the Time step, when the tab regains focus, and
  // every LIVE_REFRESH_MS while it's visible — no spinner, no cleared grid.
  // (The server re-verifies again at submit; this keeps what's shown honest.)
  useEffect(() => {
    if (step !== 3) return;
    const bump = () => { if (document.visibilityState === "visible") setLiveTick(t => t + 1); };
    bump();
    const id = setInterval(bump, LIVE_REFRESH_MS);
    document.addEventListener("visibilitychange", bump);
    window.addEventListener("focus", bump);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", bump);
      window.removeEventListener("focus", bump);
    };
  }, [step]);

  useEffect(() => {
    if (!liveTick || step !== 3 || loadingSlots) return;
    const { date, service, staff: selectedStaff } = booking;
    if (!date || !service) return;
    // Skip if the data is fresh (e.g. the date was just picked).
    if (Date.now() - lastAvailAt.current < LIVE_MIN_AGE_MS) return;
    const staffIds = availabilityStaffIds(selectedStaff, staff, allStaff);
    if (!staffIds.length) return;
    const seq = ++availSeq.current;
    lastAvailAt.current = Date.now();
    graphGetStaffAvailability(service.businessId || undefined, staffIds, date, session.tz.zone, service.id)
      .then(res => {
        // Apply only if still the latest request AND the visitor is still on
        // the Time step — never pull a picked slot out from under the Details
        // step (the server re-verifies at submit instead).
        if (seq !== availSeq.current || stepRef.current !== 3) return;
        staffAvailDay.current = ymdLocal(date);
        setStaffAvail(res);
        setSlotsError(null);
      })
      .catch(e => console.warn(`[BookingPlatform] Background availability re-check failed (keeping current slots):`, e.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveTick]);

  // ── 3b. Service availability policy (which dates / hours the service allows) ──
  // Loaded in 31-day chunks for the current service + timezone. The initial
  // scan walks forward until it finds the first open date (bounded to ~1 year,
  // Bookings' own maximum advance), so a service open only for a future event
  // week lands the calendar on that week. Months navigated to later are
  // loaded on demand. On error the calendar falls back to the legacy
  // weekday rule and slots to staff hours only — never a dead booking page.
  const svcKey = booking.service ? `${booking.service.id}|${session.tz.zone}` : null;
  const [svcAvail, setSvcAvail] = useState({ key: null, days: {}, policy: null, firstOpen: null, loading: false, error: null });
  const [calMonth, setCalMonth] = useState(null); // { year, month } shown by CalendarStep
  const svcFetchSeq = useRef(0);

  useEffect(() => {
    const service = booking.service;
    if (!service) return;
    const seq = ++svcFetchSeq.current;
    const tz = session.tz.zone;
    setSvcAvail({ key: svcKey, days: {}, policy: null, firstOpen: null, loading: true, error: null });
    (async () => {
      const start = new Date(); start.setHours(0, 0, 0, 0);
      let days = {}, policy = null, firstOpen = null;
      try {
        for (let chunk = 0; chunk < 12 && !firstOpen; chunk++) {
          const from = new Date(start); from.setDate(from.getDate() + chunk * 31);
          // Nothing is bookable past the service's maximum advance — stop there.
          if (policy?.maximumAdvanceMins != null && (from - start) / 60000 > policy.maximumAdvanceMins) break;
          const to   = new Date(from);  to.setDate(to.getDate() + 30);
          const res = await graphGetServiceAvailability(service, tz, ymdLocal(from), ymdLocal(to));
          if (seq !== svcFetchSeq.current) return;
          days = { ...days, ...res.days };
          policy = res.policy;
          firstOpen = Object.keys(res.days).sort().find(k => res.days[k].open) || null;
        }
        setSvcAvail({ key: svcKey, days, policy, firstOpen, loading: false, error: null });
      } catch (e) {
        if (seq === svcFetchSeq.current) setSvcAvail(s => ({ ...s, loading: false, error: e.message }));
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svcKey, refreshKey]);

  // Load a month on demand when the user navigates past what the scan covered.
  useEffect(() => {
    const service = booking.service;
    if (!service || !calMonth || svcAvail.key !== svcKey || svcAvail.loading || svcAvail.error) return;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const first = new Date(calMonth.year, calMonth.month, 1);
    const last  = new Date(calMonth.year, calMonth.month + 1, 0);
    const from  = first < today ? today : first;
    if (last < from) return;
    const missing = [];
    for (const d = new Date(from); d <= last; d.setDate(d.getDate() + 1)) {
      if (!(ymdLocal(d) in svcAvail.days)) missing.push(ymdLocal(d));
    }
    if (!missing.length) return;
    const seq = svcFetchSeq.current;
    graphGetServiceAvailability(service, session.tz.zone, missing[0], missing[missing.length - 1])
      .then(res => { if (seq === svcFetchSeq.current) setSvcAvail(s => ({ ...s, days: { ...s.days, ...res.days }, policy: res.policy })); })
      .catch(e => {
        // Degrade to the documented fallback (weekdays + staff hours) rather
        // than leaving the month looking fully booked. Refresh retries.
        console.warn(`[BookingPlatform] Month availability fetch failed:`, e.message);
        if (seq === svcFetchSeq.current) setSvcAvail(s => ({ ...s, error: e.message }));
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calMonth?.year, calMonth?.month, svcAvail.key, svcAvail.loading]);

  // If the pre-selected date (e.g. "today" auto-set on the Staff step) isn't
  // bookable for this service, move to the service's first open date.
  useEffect(() => {
    if (svcAvail.key !== svcKey || svcAvail.loading || svcAvail.error || !booking.date) return;
    const k = ymdLocal(booking.date);
    if (!(k in svcAvail.days) || svcAvail.days[k].open) return;
    const next = svcAvail.firstOpen;
    console.log(`[BookingPlatform] ${k} is not bookable for this service — moving to ${next || "(none)"}`);
    setBooking(b => ({ ...b, date: next ? new Date(`${next}T00:00:00`) : null, timeSlot: null }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svcAvail, booking.date?.toDateString()]);

  const serviceDays = svcAvail.key === svcKey && !svcAvail.error && !(svcAvail.loading && !Object.keys(svcAvail.days).length)
    ? svcAvail.days
    : null;


  // Slots = staff free time ∩ the service's windows for the day, on the
  // service's slot interval. Derived (not stored) so it can never be built
  // before the service policy for the day is known.
  const slots = useMemo(() => {
    const { date, service, staff: selectedStaff } = booking;
    if (!staffAvail || !date || !service) return [];
    const ymd = ymdLocal(date);
    // Service policy still loading for this day ⇒ wait rather than guess.
    if (!svcAvail.error && svcAvail.key === svcKey && !(ymd in svcAvail.days)) return [];
    const dayInfo = svcAvail.error || svcAvail.key !== svcKey ? undefined : svcAvail.days[ymd];
    const now = nowInTimeZone(session.tz.zone);
    const built = buildSlots({
      availability: staffAvail,
      ymd,
      durationMins:   parseDuration(service.defaultDuration),
      // parseDuration() defaults a missing value to 60 — buffers must default to 0.
      preBufferMins:  service.preBuffer  ? parseDuration(service.preBuffer)  : 0,
      postBufferMins: service.postBuffer ? parseDuration(service.postBuffer) : 0,
      selectedStaffId: selectedStaff && selectedStaff !== "any" ? selectedStaff.id : null,
      dayInfo,
      intervalMins: svcAvail.policy?.timeSlotIntervalMins,
      serviceHoursOverride: svcAvail.policy?.serviceHoursOverride,
      nowMinutes: now.ymd === ymd ? now.minutes : -1,
    });
    console.log(`[BookingPlatform] ${built.length} bookable slot(s) on ${ymd}`);
    return built;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffAvail, svcAvail, svcKey, booking.date?.toDateString(), booking.staff, booking.service?.id, session.tz.zone]);

  // ── 2c. Grey out days with no bookable time ──────────────────────────────────
  // The policy says which days *could* open; whether any time is actually
  // free depends on staff calendars. One range request covers the visible
  // month, and each open day runs through the same buildSlots as the time
  // list, so the calendar and the slots never disagree. On failure the days
  // stay as the policy says (the time list still shows "No time slots").
  const [monthAvail, setMonthAvail] = useState(null); // { key, data }
  const monthStaffIds = booking.service ? availabilityStaffIds(booking.staff, staff, allStaff).join(",") : "";
  const monthRange = (() => {
    if (!calMonth) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const first = new Date(calMonth.year, calMonth.month, 1);
    const last  = new Date(calMonth.year, calMonth.month + 1, 0);
    const from  = first < today ? today : first;
    return last < from ? null : { from: ymdLocal(from), to: ymdLocal(last) };
  })();
  const monthKey = monthRange && monthStaffIds
    ? `${svcKey}|${monthStaffIds}|${session.tz.zone}|${monthRange.from}|${monthRange.to}|${refreshKey}`
    : null;
  useEffect(() => {
    if (!monthKey) return;
    const service = booking.service;
    let live = true;
    graphGetStaffAvailability(service.businessId || undefined, monthStaffIds.split(","), monthRange.from, session.tz.zone, service.id, monthRange.to)
      .then(data => { if (live) setMonthAvail({ key: monthKey, data }); })
      .catch(e => console.warn(`[BookingPlatform] Month staff availability failed — days follow the service policy only:`, e.message));
    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthKey]);

  const bookableDays = useMemo(() => {
    const service = booking.service;
    if (!serviceDays || !service || !monthAvail || monthAvail.key !== monthKey) return serviceDays;
    const now = nowInTimeZone(session.tz.zone);
    const selectedYmd = booking.date ? ymdLocal(booking.date) : null;
    const out = { ...serviceDays };
    for (const [ymd, day] of Object.entries(serviceDays)) {
      if (!day.open || ymd < monthRange.from || ymd > monthRange.to) continue;
      // The selected day has fresher data (live re-checks) — use what the time list shows.
      const n = ymd === selectedYmd && staffAvail && !loadingSlots && staffAvailDay.current === ymd
        ? slots.length
        : buildSlots({
            availability: monthAvail.data,
            ymd,
            durationMins:   parseDuration(service.defaultDuration),
            preBufferMins:  service.preBuffer  ? parseDuration(service.preBuffer)  : 0,
            postBufferMins: service.postBuffer ? parseDuration(service.postBuffer) : 0,
            selectedStaffId: booking.staff && booking.staff !== "any" ? booking.staff.id : null,
            dayInfo: day,
            intervalMins: svcAvail.policy?.timeSlotIntervalMins,
            serviceHoursOverride: svcAvail.policy?.serviceHoursOverride,
            nowMinutes: now.ymd === ymd ? now.minutes : -1,
          }).length;
      if (!n) out[ymd] = { ...day, open: false };
    }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceDays, monthAvail, monthKey, slots, staffAvail, loadingSlots, booking.date?.toDateString()]);

  // The selected (e.g. auto-picked "today") day turned out fully booked ⇒
  // move to the next day with free time, while the visitor is still choosing.
  useEffect(() => {
    if (step > 3 || !booking.date || !serviceDays || bookableDays === serviceDays) return;
    const k = ymdLocal(booking.date);
    if (!serviceDays[k]?.open || bookableDays[k]?.open) return;
    const next = Object.keys(bookableDays).sort().find(d => d > k && bookableDays[d].open);
    if (next) setBooking(b => ({ ...b, date: new Date(`${next}T00:00:00`), timeSlot: null }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookableDays]);

  // Service custom questions answered from a built-in Details field (hidden
  // on the form, filled at submit) — settings.customQuestionAutofill.
  const autofilledQuestions = useMemo(
    () => resolveAutofilledQuestions(booking.service?.customQuestions, customQuestions, appSettings?.customQuestionAutofill),
    [booking.service?.id, customQuestions, appSettings]
  );

  // Slot panel shows its spinner until both staff free/busy and the service
  // policy for the selected day are in — avoids a flash of "no slots".
  const slotsPending = loadingSlots || Boolean(
    booking.date && !svcAvail.error && (svcAvail.key !== svcKey || !(ymdLocal(booking.date) in svcAvail.days))
  );

  // A background re-check can remove the time the visitor already picked
  // (someone else booked it). Clear it and say why, rather than letting them
  // carry a dead slot into the Details step.
  useEffect(() => {
    if (slotsPending || !booking.timeSlot) return;
    const fresh = slots.find(s => s.key === booking.timeSlot.key);
    if (!fresh) {
      console.log(`[BookingPlatform] Selected ${booking.timeSlot.key} is no longer available — clearing`);
      setSlotNotice("The time you picked was just taken. Please choose another.");
      setBooking(b => ({ ...b, timeSlot: null }));
    } else if (fresh !== booking.timeSlot) {
      setBooking(b => ({ ...b, timeSlot: fresh })); // keep availableStaffIds current
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots, slotsPending]);

  // ── Navigation ─────────────────────────────────────────────────────────────
  // Active (visible) wizard steps — excludes any step skipped by settings.
  // advance/goBack navigate within this filtered list so users never land on
  // a hidden step, and the "first visible" step has no Back affordance.
  const activeSteps = useMemo(() => getActiveSteps(appSettings), [appSettings]);
  const firstActiveStepIndex = activeSteps[0]?.index ?? 0;

  const advance = useCallback(() => {
    setDir(1); setAnimKey(k => k + 1);
    setStep(s => {
      const pos = activeSteps.findIndex(x => x.index === s);
      if (pos >= 0 && pos < activeSteps.length - 1) return activeSteps[pos + 1].index;
      return s + 1; // past last active step (e.g. → confirmation screen at 5)
    });
  }, [activeSteps]);

  const goBack = useCallback(() => {
    setDir(-1); setAnimKey(k => k + 1);
    setStep(s => {
      const pos = activeSteps.findIndex(x => x.index === s);
      if (pos > 0) return activeSteps[pos - 1].index;
      return s; // already at first visible step — no-op
    });
  }, [activeSteps]);
  const upd      = useCallback((k, v) => setBooking(b => ({ ...b, [k]: v })), []);
  const updForm  = useCallback((k, v) => {
    setBooking(b => ({ ...b, form: { ...b.form, [k]: v } }));
    setFormErrors(e => ({ ...e, [k]: undefined }));
    setSubmitError(null);
  }, []);
  const selectStaff = useCallback(person => {
    upd("staff", person === null ? "any" : person);
    // Auto-set date to today so availability loads immediately on the Time step
    setBooking(b => b.date ? b : { ...b, date: new Date() });
  }, [upd]);
  const refreshAvailability = useCallback(() => setRefreshKey(k => k + 1), []);
  const changeTimezone = useCallback((zone) => {
    const abbr = tzAbbr(zone);
    console.log(`[BookingPlatform] Timezone changed to ${zone} (${abbr})`);
    setSession(s => ({ ...s, tz: { zone, abbr } }));
    // Re-fetch availability with new timezone
    setRefreshKey(k => k + 1);
  }, []);

  // ── Auto-advance: single service or single staff ────────────────────────────
  // Refs track whether auto-advance has fired so back-navigation shows the step
  const autoAdvancedService = useRef(false);
  const autoAdvancedStaff = useRef(false);

  // Auto-select + advance when there's exactly 1 linked service
  useEffect(() => {
    if (step !== 1 || loadingSvc || !linkedServiceIds || autoAdvancedService.current) return;
    if (rescheduleToken && !rescheduleApplied.current) return; // the reschedule entry picks the service
    const filtered = linkedServiceIds.length
      ? services.filter(s => linkedServiceIds.includes(s.id))
      : services;
    if (filtered.length === 1 && !booking.service) {
      console.log(`[BookingPlatform] Auto-selecting single service: ${filtered[0].displayName}`);
      upd("service", filtered[0]);
      autoAdvancedService.current = true;
      // Small delay so the UI shows the selection before advancing
      setTimeout(() => { setDir(1); setAnimKey(k => k + 1); setStep(2); }, 400);
    }
  }, [step, services, linkedServiceIds, loadingSvc, booking.service, upd]);

  // Auto-select + advance when there's exactly 1 staff member for the service
  useEffect(() => {
    if (step !== 2 || loadingStaff || autoAdvancedStaff.current) return;
    if (staff.length === 1 && booking.staff === undefined) {
      console.log(`[BookingPlatform] Auto-selecting single staff: ${staff[0].displayName}`);
      selectStaff(staff[0]);
      autoAdvancedStaff.current = true;
      setTimeout(() => { setDir(1); setAnimKey(k => k + 1); setStep(3); }, 400);
    }
  }, [step, staff, loadingStaff, booking.staff, selectStaff]);

  // ── Reschedule entry from the "Manage your booking" page ────────────────────
  // /mek/:linkId?reschedule=<manage token>: load the booking being changed,
  // preselect its service, prefill the visitor's details, and start at Staff.
  // booking-create then books the new time and cancels the original.
  const [rescheduleEntry, setRescheduleEntry] = useState(null); // { booking, cancelToken } | { error }
  const rescheduleApplied = useRef(false);
  useEffect(() => {
    if (!rescheduleToken) return;
    fetch(`/api/booking/manage?t=${encodeURIComponent(rescheduleToken)}`, { cache: "no-store" })
      .then(async r => {
        const body = await r.json().catch(() => ({}));
        setRescheduleEntry(r.ok ? body : { error: body.error || "We couldn't load the booking you're rescheduling." });
      })
      .catch(() => setRescheduleEntry({ error: "We couldn't load the booking you're rescheduling." }));
  }, [rescheduleToken]);
  useEffect(() => {
    if (!rescheduleEntry || rescheduleApplied.current || loadingSvc || (linkId && linkedServiceIds === null)) return;
    rescheduleApplied.current = true;
    if (rescheduleEntry.error) { setRescheduleNotice(`${rescheduleEntry.error} You can still make a new booking below.`); return; }
    if (svcError) { setRescheduleNotice("We couldn't load this booking's services, so it can't be rescheduled right now. Please try again later."); return; }
    const prev = rescheduleEntry.booking;
    const offered = linkedServiceIds?.length ? services.filter(s => linkedServiceIds.includes(s.id)) : services;
    const svc = offered.find(s => s.id === prev.serviceId);
    if (!svc) { setRescheduleNotice("This booking link no longer offers the service you booked. You can still make a new booking below."); return; }
    autoAdvancedService.current = true;
    setBooking(b => ({
      ...b,
      service: svc,
      rescheduleFrom: { appointmentId: prev.appointmentId, businessId: prev.businessId, cancelToken: rescheduleEntry.cancelToken },
      form: { ...b.form, fullName: prev.customerName || b.form.fullName, email: prev.customerEmail || b.form.email, phone: prev.customerPhone || b.form.phone },
    }));
    setRescheduleNotice(`You're rescheduling your ${svc.displayName} booking on ${new Date(prev.startUtc).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}. It stays booked until you confirm the new time.`);
    // The Relationship step (when shown) still needs an answer for the new
    // booking's Lead — start there; otherwise go straight to Staff.
    const startStep = activeSteps.some(st => st.index === 0) ? firstActiveStepIndex : 2;
    setDir(1); setAnimKey(k => k + 1); setStep(startStep);
  }, [rescheduleEntry, loadingSvc, linkId, linkedServiceIds, services, svcError, activeSteps, firstActiveStepIndex]);

  // Reset auto-advance flags when booking is reset
  const resetAutoAdvance = useCallback(() => {
    autoAdvancedService.current = false;
    autoAdvancedStaff.current = false;
  }, []);

  // ── Confirmation actions ───────────────────────────────────────────────────
  const resetBooking = useCallback(() => {
    setRescheduleNotice(null);
    setBooking({ relationshipType:null, service:null, staff:undefined, date:null, timeSlot:null, appointmentId:null, cancelToken:null, businessId:null, rescheduleFrom:null, rescheduled:false, previousStillActive:false, form:{ fullName:"", email:"", company:"", phone:"", notes:"" } });
    setSubmitError(null); setFormErrors({});
    resetAutoAdvance();
    setDir(-1); setAnimKey(k=>k+1); setStep(firstActiveStepIndex);
  }, [resetAutoAdvance, firstActiveStepIndex]);
  const handleReschedule = useCallback(() => {
    // Keep the selected service but reset date/time/staff so user can pick a new slot
    // Keep the current booking as the one to replace: the server books the new
    // time first, then cancels this one (so the visitor is never left with none).
    setBooking(b => ({
      ...b, staff:undefined, date:null, timeSlot:null,
      // Only with a cancel token — without one the server can't replace it,
      // so the new booking is made as a fresh one.
      rescheduleFrom: b.appointmentId && b.cancelToken
        ? { appointmentId: b.appointmentId, businessId: b.businessId || undefined, cancelToken: b.cancelToken }
        : b.rescheduleFrom,
      appointmentId:null, cancelToken:null, businessId:null, assignedStaff:null, rescheduled:false, previousStillActive:false,
    }));
    setSubmitError(null); setFormErrors({});
    setDir(-1); setAnimKey(k=>k+1); setStep(2);
  }, []);
  // Resolves only once Bookings has actually cancelled the appointment; rejects
  // otherwise, so the confirmation screen never claims a cancel that failed.
  const handleCancel = useCallback(async () => {
    if (!booking.appointmentId) throw new Error("We couldn't find this booking to cancel.");
    try {
      await graphCancelAppointment(booking.appointmentId, booking.businessId || undefined, booking.cancelToken);
      console.log("[BookingPlatform] Appointment cancelled successfully");
    } catch (err) {
      console.error("[BookingPlatform] Cancel failed:", err.message);
      throw err;
    }
  }, [booking.appointmentId, booking.businessId, booking.cancelToken]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const submit = async () => {
    const errs = validateForm(booking.form, booking.service, customQuestions, autofilledQuestions);
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      setSubmitError("Please complete the required fields above.");
      return;
    }
    if (!booking.date || !booking.timeSlot) {
      // Defensive: no time selected (e.g. it was cleared) — back to the calendar.
      setSlotNotice("Please pick a time for your booking.");
      setDir(-1); setAnimKey(k => k + 1); setStep(3);
      return;
    }
    setSubmitting(true); setSubmitError(null);
    try {
      const staffId = booking.staff && booking.staff !== "any" ? booking.staff.id : null;
      const staffEmail = booking.staff && booking.staff !== "any" ? booking.staff.emailAddress : null;

      // Build custom question answers from form + extract semantic lead fields
      // (Job Title, ERP, Relationship Type in skipService mode) by matching
      // question ID (preferred) or displayName keywords (fallback).
      let jobTitleAnswer;
      let erpAnswer;
      let relationshipAnswerText;
      const rtCfg = appSettings?.steps?.relationshipType;
      const skipService = rtCfg?.mode === "skipService";
      const configuredRelQId = skipService ? (rtCfg.serviceQuestionId || "") : "";

      const cqAnswers = (booking.service.customQuestions || [])
        .map(sq => {
          const fromField = autofilledQuestions[sq.questionId];
          const val = fromField ? booking.form[fromField] : booking.form[`cq_${sq.questionId}`];
          if (!val) return null;
          if (fromField) return { questionId: sq.questionId, answer: val }; // a built-in field's value — not a job/ERP/relationship answer
          const def = customQuestions.find(q => q.id === sq.questionId);
          const name = (def?.displayName || "").toLowerCase();
          if (/job|title|role|position/.test(name)) jobTitleAnswer = val;
          if (/erp|system|platform/.test(name)) erpAnswer = val;
          if (skipService && !relationshipAnswerText) {
            if (configuredRelQId && sq.questionId === configuredRelQId) {
              relationshipAnswerText = val;
            } else if (!configuredRelQId && /are you a|relationship/.test(name)) {
              relationshipAnswerText = val;
            }
          }
          return { questionId: sq.questionId, answer: val };
        })
        .filter(Boolean);

      if (skipService && !relationshipAnswerText) {
        console.warn("[BookingPlatform] skipService: no relationship answer found on selected service — Lead will be created without mek_relationshiptype");
      }

      // Strip any UTMs configured as "hide" in app settings before sending.
      // Backend also enforces this — client filter is for belt-and-suspenders.
      const filteredAttr = (() => {
        const utmCfg = appSettings?.utmParameters;
        if (!utmCfg) return session.attr;
        const next = { ...session.attr };
        for (const [key, cfg] of Object.entries(utmCfg)) {
          if (cfg?.mode === "hide") delete next[key];
        }
        return next;
      })();

      const result = await graphCreateAppointment({
        sessionId:     session.correlationId,
        attr:          filteredAttr,
        timezone:      session.tz.zone,
        businessId:    booking.service.businessId || "", // empty ⇒ server uses the Settings default
        serviceId:     booking.service.id,
        serviceName:   booking.service.displayName,
        staffMemberId: staffId,
        staffEmail:    staffEmail,
        durationMinutes: parseDuration(booking.service.defaultDuration),
        startDateTime: { dateTime:`${ymdLocal(booking.date)}T${booking.timeSlot.key}:00`, timeZone: session.tz.zone },
        attendee:      {
          fullName: booking.form.company
            ? `${booking.form.fullName} : ${booking.form.company}`
            : booking.form.fullName,
          email:     booking.form.email,
          company:   booking.form.company,
          phone:     booking.form.phone,
          notes:     booking.form.notes,
          jobTitle:  jobTitleAnswer,
          erpSystem: erpAnswer,
        },
        customQuestionAnswers: cqAnswers,
        linkId: linkId || undefined,
        pageOrigin: window.location.origin, // for the "Manage your booking" email link (server prefers BOOKING_HOST_URL)
        relationshipTypeValue: booking.relationshipType?.value,
        relationshipAnswerText,
        customParameters: customParamValues,
        ...(booking.rescheduleFrom ? { reschedule: booking.rescheduleFrom } : {}),
      });
      setBooking(b => ({
        ...b,
        appointmentId: result.appointmentId,
        cancelToken: result.cancelToken || null,
        businessId: result.businessId || null,
        assignedStaff: result.assignedStaff || null,
        rescheduleFrom: null,
        rescheduled: Boolean(result.rescheduled),
        previousStillActive: result.rescheduled === true && result.previousCancelled === false,
      }));
      setRescheduleNotice(null);
      setSubmitting(false);
      setDir(1); setAnimKey(k=>k+1); setStep(5);
    } catch(e) {
      setSubmitting(false);
      if (e.code === "SLOT_UNAVAILABLE") {
        // The time fell outside the service's availability since it was
        // shown (e.g. the page sat open past the lead time). Send the visitor
        // back to pick again with fresh availability; their details are kept.
        setSlotNotice(e.message);
        setBooking(b => ({ ...b, timeSlot: null }));
        setRefreshKey(k => k + 1);
        setDir(-1); setAnimKey(k => k + 1); setStep(3);
        return;
      }
      if (e.code === "STAFF_REQUIRED" || e.code === "STAFF_SELECTION_NOT_ALLOWED") {
        // The server's staff rule differs from what this page showed (settings
        // or Bookings changed while it was open). Adopt the server's rule for
        // this service and send the visitor back to choose; details are kept.
        const anyoneOnly = e.code === "STAFF_SELECTION_NOT_ALLOWED";
        setChoiceOverride({ serviceId: booking.service?.id, patch: anyoneOnly
          ? { allowSpecific: false, allowAnyone: true, anyoneOnly: true }
          : { allowAnyone: false, anyoneOnly: false } });
        setStaffNotice(e.message);
        setBooking(b => ({ ...b, staff: anyoneOnly ? "any" : undefined, timeSlot: null }));
        setDir(-1); setAnimKey(k => k + 1); setStep(2);
        return;
      }
      if (e.code === "CANCEL_NOT_AUTHORIZED" && booking.rescheduleFrom) {
        // The original booking couldn't be verified (e.g. its cancel link
        // expired). Don't loop: drop the reschedule so the next click books
        // this as a new appointment, and say so.
        setBooking(b => ({ ...b, rescheduleFrom: null }));
        setRescheduleNotice(null);
        setSubmitError(`${e.message} Click "Confirm booking" again to book this as a new appointment — your original booking stays as it is.`);
        return;
      }
      setSubmitError("Booking failed — please try again.");
    }
  };

  // ── Advance guard ──────────────────────────────────────────────────────────
  const canAdvance = [
    () => !!booking.relationshipType,
    () => !!booking.service,
    () => booking.staff !== undefined,
    () => !!booking.date && !!booking.timeSlot,
    () => true,
  ][step]?.() ?? false;

  // ── Initial-load gate ─────────────────────────────────────────────────────
  // Hold back the wizard UI until everything needed for the FIRST VISIBLE step
  // is in place — link resolved, services + relationship types fetched, and
  // the skip decision made. This prevents the "fetching services…" spinner
  // and the brief flash of soon-to-be-skipped steps (e.g. Type when skipService
  // is configured) from leaking into the user experience.
  const isAppReady =
    linkedServiceIds !== null &&
    !loadingSvc &&
    !loadingRelTypes &&
    skipChecked;

  // Rotate the status message every ~1.8s while loading.
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
  useEffect(() => {
    if (isAppReady) return;
    const t = setInterval(() => setLoadingMsgIdx(i => (i + 1) % LOADING_MESSAGES.length), 1800);
    return () => clearInterval(t);
  }, [isAppReady]);

  // Hard timeout at 15s — surface a retry rather than spin forever.
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  useEffect(() => {
    if (isAppReady || loadingTimedOut) return;
    const t = setTimeout(() => setLoadingTimedOut(true), 15000);
    return () => clearTimeout(t);
  }, [isAppReady, loadingTimedOut]);

  // If the booking link couldn't be resolved, show the error landing page
  if (linkError) return <BookingLanding error={linkError} disabled={linkDisabled} />;

  if (!isAppReady) {
    if (loadingTimedOut) return <LoadingTimeoutScreen />;
    return <LoadingScreen message={LOADING_MESSAGES[loadingMsgIdx]} />;
  }

  return (
    <>
      <style>{`
        .slide-in { animation: ${dir >= 0 ? "slideRight" : "slideLeft"} .32s cubic-bezier(.22,.61,.36,1) both; will-change: transform, opacity; }
        .page-in { animation: pageIn .35s ease-out both; }
        *, *::before, *::after { box-sizing: border-box; }
        button { transition: filter .12s; }
        button:not(:disabled):hover { filter: brightness(.96); }
        button:not(:disabled):active { transform: scale(.99); }
        input::placeholder, textarea::placeholder { color: #CBD5E1; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        @media (max-width: 640px) {
          .booking-layout { flex-direction: column !important; }
          .booking-sidebar { width: 100% !important; order: -1 !important; }
          .calendar-layout { grid-template-columns: 1fr !important; }
          .slot-grid { grid-template-columns: 1fr 1fr !important; }
          .details-grid { grid-template-columns: 1fr !important; }
          .confirm-grid { grid-template-columns: 1fr !important; gap: 10px 0 !important; }
          .progress-label { display: none !important; }
          .progress-connector { width: 20px !important; }
        }
      `}</style>

      <div className="page-in" style={{ minHeight:"100vh", background:"#F5FAFA", fontFamily:"-apple-system,'Segoe UI',system-ui,sans-serif", display:"flex", alignItems:"flex-start", justifyContent:"center", padding: isMobile ? "16px 10px 40px" : "40px 16px 80px" }}>
        <div style={{ width:"100%", maxWidth:840 }}>

          {/* Header */}
          <div style={{ position:"relative", textAlign:"center", marginBottom:28, animation:"fadeUp .4s ease both" }}>
            <div style={{ display:"inline-flex", flexDirection:"column", alignItems:"center", gap:8 }}>
              <MekormaLogo height={isMobile ? 24 : 32}/>
              <p style={{ fontSize:13, color:"#64748B", margin:0, letterSpacing:"0.01em" }}>{CONFIG.tagline}</p>
            </div>
          </div>

          {step < 5 && <Progress step={step} activeSteps={activeSteps}/>}

          <div className="booking-layout" style={{ display:"flex", gap: isMobile ? 12 : 16, alignItems:"flex-start" }}>
            {/* Main card */}
            <div style={{ flex:1, background:"#fff", border:"1.5px solid #E2E8F0", borderRadius: isMobile ? 12 : 16, padding: isMobile ? "16px 14px" : "24px 24px", animation:"fadeUp .4s .05s ease both", minWidth:0 }}>
              <div key={animKey} className="slide-in">
                {(() => {
                  // Gate photos and timezone picker on feature toggles from app settings.
                  const photosEnabled      = appSettings?.featureToggles?.enableProfilePhotos !== false;
                  const timezoneEnabled    = appSettings?.featureToggles?.enableTimezonePicker !== false;
                  const effAllPhotos       = photosEnabled ? allStaffPhotos : {};
                  const effStaffPhotos     = photosEnabled ? staffPhotos   : {};
                  const calendarPhotos     = photosEnabled
                    ? (Object.keys(staffPhotos).length ? staffPhotos : allStaffPhotos)
                    : {};
                  return <>
                    {step === 0 && <RelationshipStep options={relationshipTypes} loading={loadingRelTypes} error={relTypesError} selected={booking.relationshipType} onSelect={rt => { upd("relationshipType",rt); if (!booking.rescheduleFrom) { upd("service",null); upd("staff",undefined); } }}/>}
                    {rescheduleNotice && step <= 4 && (
                      <div role="status" style={{ background:"#F0FDF4", border:"1px solid #86EFAC", borderRadius:10, padding:"10px 12px", marginBottom:14, fontSize:12, color:"#166534", lineHeight:1.5 }}>
                        {rescheduleNotice}
                      </div>
                    )}
                    {step === 1 && <ServiceStep relType={booking.relationshipType} services={linkedServiceIds && linkedServiceIds.length ? services.filter(s => linkedServiceIds.includes(s.id)) : services} loading={loadingSvc || (linkId && linkedServiceIds === null)} error={svcError} selected={booking.service} onSelect={svc => upd("service", svc)} allStaff={allStaff} staffPhotos={effAllPhotos} appSettings={appSettings}/>}
                    {step === 2 && <StaffStep service={booking.service} staff={staff} loadingStaff={loadingStaff} staffError={staffError} selected={booking.staff==="any"?"any":booking.staff} onSelect={p => { setStaffNotice(null); selectStaff(p); }} staffPhotos={effStaffPhotos} notice={staffNotice} choice={{ ...staffChoiceFor(appSettings, booking.service), ...(choiceOverride?.serviceId === booking.service?.id ? choiceOverride.patch : {}) }}/>}
                    {step === 3 && <CalendarStep service={booking.service} staff={staff} selectedStaff={booking.staff} selectedDate={booking.date} selectedSlot={booking.timeSlot} onDateSelect={d=>{ setSlotNotice(null); upd("date",d); }} onSlotSelect={s=>{ setSlotNotice(null); upd("timeSlot",s); }} slotNotice={slotNotice} loadingSlots={slotsPending} slots={slots} slotsError={slotsError} serviceDays={bookableDays} serviceDaysLoading={svcAvail.loading && !svcAvail.error} onMonthChange={setCalMonth} tz={session.tz} onRefresh={refreshAvailability} onTimezoneChange={changeTimezone} allStaff={allStaff} staffPhotos={calendarPhotos} showTimezonePicker={timezoneEnabled}/>}
                    {step === 4 && <DetailsStep form={booking.form} onChange={updForm} errors={formErrors} service={booking.service} customQuestions={customQuestions} autofilled={autofilledQuestions} disabled={submitting}/>}
                    {step === 5 && <ConfirmationStep booking={booking} session={session} onReschedule={handleReschedule} onCancel={handleCancel} onNewBooking={resetBooking} staff={staff} staffPhotos={effStaffPhotos}/>}
                  </>;
                })()}
              </div>

              {step < 5 && (() => {
                const activePos = activeSteps.findIndex(x => x.index === step);
                const canBack = activePos > 0;
                return (
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:24, paddingTop:16, borderTop:"1px solid #F1F5F9" }}>
                  <div>{canBack && <button onClick={goBack} disabled={submitting} style={{ ...S.btn("secondary"), ...(submitting ? { opacity:0.5, cursor:"default" } : {}) }}><Ic.ChevL s={14}/> Back</button>}</div>
                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                    {submitError && <span style={{ fontSize:12, color:"#EF4444" }}>{submitError}</span>}
                    {step < 4 && <button onClick={() => canAdvance && advance()} disabled={!canAdvance} style={S.btn("primary",!canAdvance)}>Continue <Ic.ChevR s={14}/></button>}
                    {step === 4 && (
                      <button onClick={submit} disabled={submitting} style={{ ...S.btn("primary",submitting), minWidth:150 }}>
                        {submitting ? <><Ic.Loader s={15}/> Booking…</> : <>Confirm booking <Ic.ChevR s={14}/></>}
                      </button>
                    )}
                  </div>
                </div>
                );
              })()}
            </div>

            {/* Sidebar — show once the attendee has advanced past the first visible step. */}
            {(() => {
              const activePos = activeSteps.findIndex(x => x.index === step);
              return activePos >= 1 && step < 5 && !isMobile;
            })() && (
              <div className="booking-sidebar" style={{ width:186, flexShrink:0, animation:"fadeUp .4s .1s ease both" }}>
                <Sidebar booking={booking} tz={session.tz} correlationId={session.correlationId}/>
              </div>
            )}
          </div>

          {step < 5 && (
            <div style={{ textAlign:"center", marginTop:20 }}>
              <p style={{ fontSize:11, color:"#CBD5E1" }}>
                <span style={{ color:"#88c96c", fontWeight:700 }}>Mekorma</span> · Microsoft Bookings + Graph API · Dynamics 365
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
